import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { regionPaths } from '../lib/italyMap'
import { regionsData } from '../lib/types'
import type { Nurse, Patient, Appointment } from '../lib/types'
import AiSmartMatcher from './AiSmartMatcher'
import {
  Users, FileText, Shield, MapPin, Search, ChevronRight,
  ArrowLeft, Globe, Award, Plus, Edit3, Trash2, X,
  Loader2, AlertCircle, Check, Phone, Mail, Building2,
  Download, FileSpreadsheet, Clock, MessageSquare, Briefcase, ExternalLink,
  Navigation, Crosshair, Euro, Calendar, Heart, User, ShieldCheck, Sparkles
} from 'lucide-react'

// @ts-ignore
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
// @ts-ignore
import Docxtemplater from 'docxtemplater'
// @ts-ignore
import PizZip from 'pizzip'
import { sendAptosFormattedEmail } from '../lib/emailUtils'

// TypeScript declarations for CDN-loaded libraries
declare const Tesseract: any
declare const pdfjsLib: any

// Helper: convert a file (image or PDF) to an image URL that Tesseract can process
async function fileToDashboardImageUrl(file: File): Promise<string> {
  const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
  if (!isPdf) {
    return URL.createObjectURL(file)
  }
  if (typeof pdfjsLib === 'undefined') {
    throw new Error('Libreria PDF non caricata. Ricarica la pagina e riprova.')
  }
  const arrayBuffer = await file.arrayBuffer()
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise
  const page = await pdf.getPage(1)
  const scale = 3.5 // Increased from 2.0 for better OCR accuracy
  const viewport = page.getViewport({ scale })
  const canvas = document.createElement('canvas')
  canvas.width = viewport.width
  canvas.height = viewport.height
  const ctx = canvas.getContext('2d')!
  await page.render({ canvasContext: ctx, viewport }).promise
  return new Promise<string>((resolve) => {
    canvas.toBlob((blob) => {
      resolve(URL.createObjectURL(blob!))
    }, 'image/png')
  })
}

type ViewMode = 'regions' | 'provinces' | 'nurse_list'

export function Dashboard() {
  const { isStaffPlus, isAdmin, isSuperAdmin, user } = useAuth()
  const [nurses, setNurses] = useState<Nurse[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [showArchiviati, setShowArchiviati] = useState(false)
  const [viewMode, setViewMode] = useState<ViewMode>('regions')
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null)
  const [selectedProvince, setSelectedProvince] = useState<string | null>(null)
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null)
  const [kpiFilter, setKpiFilter] = useState<'all' | 'contratti' | 'assicurazioni' | null>(null)
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 })
  const [showNurseForm, setShowNurseForm] = useState(false)
  const [editingNurse, setEditingNurse] = useState<Nurse | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [selectedNurse, setSelectedNurse] = useState<Nurse | null>(null)

  // Geo-search state
  const [geoSearchAddress, setGeoSearchAddress] = useState('')
  const [geoSearching, setGeoSearching] = useState(false)
  const [geoResults, setGeoResults] = useState<{ nurse: Nurse; distanceKm: number; inRange: boolean; zoneMatch: boolean }[]>([])
  const [showGeoSearch, setShowGeoSearch] = useState(false)
  const [geoError, setGeoError] = useState('')
  const [showAiMatcher, setShowAiMatcher] = useState(false)
  const [normalizingAddress, setNormalizingAddress] = useState(false)
  
  // A.I. Generative UI State
  const [draftingAIEmail, setDraftingAIEmail] = useState(false)
  const [draftingAIContract, setDraftingAIContract] = useState(false)

  // Billing data for selected nurse
  const [nurseBilling, setNurseBilling] = useState<{ totaleLordo: number, totaleOre: number, totaleAccessi: number } | null>(null)
  const [nursePatients, setNursePatients] = useState<{ patient: Patient, appointments: Appointment[] }[]>([])
  
  useEffect(() => {
    if (selectedNurse) {
      async function fetchNurseBilling() {
        const { data } = await supabase.from('accesses').select('ore, costo_orario, rimborso_km').eq('nurse_id', selectedNurse?.id)
        if (data) {
          const stats = data.reduce((acc: any, curr: any) => {
            acc.totaleAccessi++
            acc.totaleOre += curr.ore || 0
            acc.totaleLordo += ((curr.ore || 0) * (curr.costo_orario || 0)) + (curr.rimborso_km || 0)
            return acc
          }, { totaleLordo: 0, totaleOre: 0, totaleAccessi: 0 })
          setNurseBilling(stats)
        } else {
          setNurseBilling({ totaleLordo: 0, totaleOre: 0, totaleAccessi: 0 })
        }
      }
      fetchNurseBilling()
      // Fetch patients
      async function fetchNursePatients() {
        const { data: pts } = await supabase.from('patients').select('*').eq('nurse_id', selectedNurse?.id).order('cognome')
        if (pts && pts.length > 0) {
          const patIds = pts.map((p: Patient) => p.id)
          const { data: appts } = await supabase.from('appointments').select('*').in('patient_id', patIds).order('ordine')
          setNursePatients(pts.map((p: Patient) => ({ patient: p, appointments: (appts || []).filter((a: Appointment) => a.patient_id === p.id) })))
        } else {
          setNursePatients([])
        }
      }
      fetchNursePatients()
    } else {
      setNurseBilling(null)
      setNursePatients([])
    }
  }, [selectedNurse])

  // Nurse Tabs and Diary State
  const [nurseDetailTab, setNurseDetailTab] = useState<'anagrafica'|'professionale'|'diario'|'documenti'|'pazienti'>('anagrafica')
  const [nurseDiary, setNurseDiary] = useState<any[]>([])
  const [nurseDiaryForm, setNurseDiaryForm] = useState({ titolo: '', contenuto: '', tipo: 'appunto', data: new Date().toISOString().slice(0,10) })
  const [editingNurseDiaryId, setEditingNurseDiaryId] = useState<string | null>(null)

  // Fetch Nurse Diary
  async function fetchNurseDiary(nId: string) {
    const { data } = await supabase.from('nurse_diary').select('*').eq('nurse_id', nId).order('data', { ascending: false })
    if (data) setNurseDiary(data)
  }

  // Handle Add / Edit
  async function handleAddNurseDiaryEntry(nId: string) {
    if (!nurseDiaryForm.titolo.trim()) { setError('Inserisci un titolo per la nota'); return }
    const payload: any = {
      nurse_id: nId,
      titolo: nurseDiaryForm.titolo,
      contenuto: nurseDiaryForm.contenuto,
      tipo: nurseDiaryForm.tipo,
      data: nurseDiaryForm.data
    }
    
    if (editingNurseDiaryId) {
      const { error: err } = await supabase.from('nurse_diary').update(payload).eq('id', editingNurseDiaryId)
      if (err) { setError(err.message); return }
      setSuccess('📝 Nota modificata correttamente')
    } else {
      payload.created_by = user?.displayName || user?.email || 'Sistema'
      const { error: err } = await supabase.from('nurse_diary').insert(payload)
      if (err) { setError(err.message); return }
      setSuccess('📝 Nota aggiunta al diario')
    }
    
    setEditingNurseDiaryId(null)
    setNurseDiaryForm({ titolo: '', contenuto: '', tipo: 'appunto', data: new Date().toISOString().slice(0,10) })
    fetchNurseDiary(nId)
  }

  async function handleDeleteNurseDiaryEntry(entryId: string, nId: string) {
    if (!confirm('Eliminare questa nota dal diario?')) return
    await supabase.from('nurse_diary').delete().eq('id', entryId)
    fetchNurseDiary(nId)
    setSuccess('Nota eliminata')
  }

  // Inline note for operator profile (legacy compatibility)
  const [nurseNote, setNurseNote] = useState('')

  // Document archive state
  const [nurseDocuments, setNurseDocuments] = useState<any[]>([])
  const [loadingDocs, setLoadingDocs] = useState(false)
  const [uploadingDoc, setUploadingDoc] = useState(false)
  const [docCategory, setDocCategory] = useState('altro')

  const DOC_CATEGORIES = [
    { key: 'cv', label: 'Curriculum Vitae', icon: '📄' },
    { key: 'contratto', label: 'Contratto', icon: '📝' },
    { key: 'assicurazione', label: 'Assicurazione', icon: '🛡️' },
    { key: 'albo', label: 'Iscrizione Albo', icon: '🏅' },
    { key: 'documento_id', label: 'Documento Identità', icon: '🪣' },
    { key: 'codice_fiscale', label: 'Codice Fiscale', icon: '💳' },
    { key: 'altro', label: 'Altro', icon: '📁' },
  ]

  // OCR state
  const [ocrProcessing, setOcrProcessing] = useState(false)
  const [ocrProgress, setOcrProgress] = useState('')

  // Empty form template
  const emptyForm: Partial<Nurse> = {
    nome: '', cognome: '', codice_fiscale: '', partita_iva: '', telefono: '', email: '',
    regione: '', provincia: '', indirizzo: '', residenza_contratto: '', qualifica: 'Infermiere',
    contratto: '', contratto_firmato: false, assicurazione: '',
    assicurazione_scadenza: '', polizza_massimale: '', opi_provincia: '', opi_numero: '',
    data_abilitazione: '', data_iscrizione_albo: '', note: '',
    pec: '', tipo_contratto: '', km_spostamento: '', zone_coperte: '',
    disponibilita: '', fasce_orarie: '',
  }
  const [formData, setFormData] = useState<Partial<Nurse>>(emptyForm)

  useEffect(() => { fetchNurses() }, [])

  async function fetchNurses() {
    setIsLoading(true)
    const { data, error } = await supabase
      .from('nurses')
      .select('*')
      .order('cognome', { ascending: true })

    if (error) {
      console.error('Error fetching nurses:', error)
      setError('Errore nel caricamento dei dati')
    } else {
      setNurses(data as Nurse[])
    }
    setIsLoading(false)
  }

  async function fetchNurseDocuments(nurseId: string) {
    setLoadingDocs(true)
    const { data, error } = await supabase
      .from('documents')
      .select('*')
      .eq('nurse_id', nurseId)
      .order('created_at', { ascending: false })
    if (error) console.error('Error fetching docs:', error)
    else setNurseDocuments(data || [])
    setLoadingDocs(false)
  }

  async function handleUploadDocument(nurseId: string, file: File) {
    setUploadingDoc(true)
    try {
      const filePath = `nurses/${nurseId}/${Date.now()}_${file.name}`
      const { error: uploadErr } = await supabase.storage
        .from('documents')
        .upload(filePath, file)

      let url = ''
      if (!uploadErr) {
        const { data: urlData } = supabase.storage.from('documents').getPublicUrl(filePath)
        url = urlData?.publicUrl || ''
      }

      const { error: insertErr } = await supabase.from('documents').insert({
        nurse_id: nurseId,
        nome: file.name,
        categoria: docCategory,
        url: url,
        file_size: file.size,
        mime_type: file.type,
      })
      if (insertErr) throw insertErr

      setSuccess(`Documento "${file.name}" caricato!`)
      fetchNurseDocuments(nurseId)
    } catch (err: any) {
      setError('Errore upload: ' + (err.message || 'sconosciuto'))
    } finally {
      setUploadingDoc(false)
    }
  }

  async function handleDeleteDocument(docId: string, nurseId: string) {
    if (!confirm('Eliminare questo documento?')) return
    const { error: err } = await supabase.from('documents').delete().eq('id', docId)
    if (err) setError('Errore: ' + err.message)
    else fetchNurseDocuments(nurseId)
  }

  // OCR auto-fill for nurse form
  // Advanced File Text Extractor for CVs and Documents
  async function extractTextFromFile(file: File, updateProgress: (msg: string) => void): Promise<string> {
    const ext = file.name.split('.').pop()?.toLowerCase() || ''
    
    // 1. Text files
    if (['txt', 'csv'].includes(ext)) {
      updateProgress('Lettura testuale diretta...')
      return await file.text()
    }
    
    // 2. DOCX Word files
    if (ext === 'docx') {
      try {
        updateProgress('Estrazione nativa Word...')
        const arrayBuffer = await file.arrayBuffer()
        const JSZip = (window as any).JSZip
        if (!JSZip) throw new Error('JSZip non caricato')
        const zip = await JSZip.loadAsync(arrayBuffer)
        const docXml = await zip.file('word/document.xml')?.async('string')
        if (docXml) {
          const text = docXml.replace(/<w:p /g, '\n<w:p ').replace(/<[^>]+>/g, ' ')
          if (text.trim().length > 50) return text
        }
      } catch (e) {
        console.warn('Errore lettura DOCX, provo OCR:', e)
      }
    }

    return ""
  }

    // Helper to convert File to Gemini inlineData format
  async function fileToGenerativePart(file: File): Promise<{ inlineData: { data: string, mimeType: string } }> {
    if (file.type.includes('word') || file.name.endsWith('.doc') || file.name.endsWith('.docx')) {
      throw new Error("I file Word (.docx, .doc) non sono supportati. Per favore converti il file in PDF o usa un'immagine.");
    }
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64Data = (reader.result as string).split(',')[1];
        let mimeType = file.type || 'application/pdf';
        resolve({
          inlineData: {
            data: base64Data,
            mimeType
          }
        });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // --- A.I. Address Normalization ---
  async function handleNormalizeAddress() {
    if (!formData.indirizzo || formData.indirizzo.trim() === '') return;
    
    let apiKey = localStorage.getItem('GEMINI_API_KEY');
    if (!apiKey) {
      alert("API Key Gemini mancante! Inseriscila nelle Impostazioni.");
      return;
    }
    
    setNormalizingAddress(true);
    try {
      const promptText = `Sei un esperto operatore logistico e geografo italiano. Normalizza il seguente indirizzo stringato o incompleto.\nIndirizzo grezzo: "${formData.indirizzo}"\n\nRegole:\n1. Restituisci SOLO un JSON.\n2. Correggi abbreviazioni (es. vle mza -> Viale Monza).\n3. Compila anche regione, provincia (sigla o estesa corrette). Restituisci formato: {"indirizzo_corretto": "...", "provincia": "...", "regione": "..."}`;
      
      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contents: [{ parts: [{ text: promptText }] }] })
      });
      
      if (!response.ok) throw new Error('Network error');
      const data = await response.json();
      let rawText = data.candidates?.[0]?.content?.parts?.[0]?.text || '';
      rawText = rawText.replace(/```json/g, '').replace(/```/g, '').trim();
      const parsed = JSON.parse(rawText);
      
      if (parsed.indirizzo_corretto) {
        setFormData(prev => ({
          ...prev,
          indirizzo: parsed.indirizzo_corretto,
          provincia: parsed.provincia || prev.provincia,
          regione: parsed.regione || prev.regione
        }));
        setSuccess('📍 Indirizzo normalizzato con successo via A.I.');
      }
    } catch (e) {
      console.error(e);
      setError('Errore durante la normalizzazione.');
    } finally {
      setNormalizingAddress(false);
    }
  }

  // --- A.I. Email Drafter ---
  async function handleAiDraftEmail(nurse: Nurse, docs: any[]) {
    let apiKey = localStorage.getItem('GEMINI_API_KEY');
    if (!apiKey) {
      alert("API Key Gemini mancante! Inseriscila nelle Impostazioni.");
      return;
    }
    setDraftingAIEmail(true);
    setSuccess("Recupero del tuo template ufficiale e analisi documenti mancanti...");
    try {
      const { data: dbTemplates } = await supabase.from('email_templates').select('*');
      const isRitenuta = nurse.tipo_contratto?.toLowerCase().includes('ritenuta') || nurse.tipo_contratto?.toLowerCase().includes('occasional');
      const targetType = isRitenuta ? 'richiesta_documenti_ritenuta' : 'richiesta_documenti_piva';
      
      const rawTemplate = dbTemplates?.find((t: any) => t.tipo === targetType);
      
      let baseOggetto = rawTemplate?.oggetto?.replace(/\{\{nome\}\}/gi, `${nurse.cognome} ${nurse.nome}`) || `Richiesta Documenti - ${nurse.cognome} ${nurse.nome}`;
      let baseCorpo = rawTemplate?.corpo?.replace(/<[^>]+>/g, '') || `Gentile ${nurse.cognome} ${nurse.nome}, Le chiediamo di inviare i documenti mancanti.\n\nCordiali saluti.`; 
      const docNames = docs.map(d => d.categoria).join(', ');
      
      const promptText = `Sei l'assistente HR di QuiCare. Questo è il nostro template UFFICIALE aziendale per la richiesta di documenti:

*** INIZIO TEMPLATE ***
${baseCorpo}
*** FINE TEMPLATE ***

Documenti attualmente presentati dall'operatore in archivio: ${docNames || 'Nessuno'}.

Il tuo compito:
Trasforma il template fornito rimuovendo DALL'ELENCO PUNTATO DEI DOCUMENTI RICHIESTI solo ed esclusivamente quelli che l'utente HA GIA' caricato fra i summenzionati (Ad esempio se ha già CV, rimuovi CV dall'elenco).
Tutto il resto del testo (saluti iniziali, scuse, firme e considerazioni) deve rimanere IDENTICO al template originale, parola per parola. Non inventarti pezzi nuovi o markdown non necessari.
Sostituisci "{{nome}}" nel testo con "${nurse.cognome} ${nurse.nome}" se la variabile è rimasta.
Se l'assicurazione risulta caricata ma "assicurazione_scadenza" (${nurse.assicurazione_scadenza || 'Mancante'}) è superata, mantieni l'assicurazione nell'elenco specificando brevemente che è scaduta.

Output: Restituisci SOLO IL CORPO dell'email pronto. Nessun testo di contorno.`;
      
      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contents: [{ parts: [{ text: promptText }] }] })
      });
      if (!response.ok) throw new Error('Network error');
      const data = await response.json();
      let aiText = data.candidates?.[0]?.content?.parts?.[0]?.text || '';
      aiText = aiText.replace(/\*/g, ''); // Fix asterisks
      
      const subject = encodeURIComponent(baseOggetto);
      const body = encodeURIComponent(aiText.trim());
      const email = nurse.email ? nurse.email : nurse.pec ? nurse.pec : '';
      
      if (!email) throw new Error('Operatore senza indirizzo email!');
      
      sendAptosFormattedEmail(
        email, 
        baseOggetto, 
        aiText.trim(), 
        '', 
        (msg: string) => setSuccess('Magia A.I. completata! Bozza email cordiale preparata con font Aptos 12.')
      )
      
    } catch (e: any) {
      setError(e.message || 'Errore A.I. generazione email.');
    } finally {
      setDraftingAIEmail(false);
    }
  }

  // --- Official Contract Generator ---
  async function handleAiDraftContract(nurse: Nurse) {
    setDraftingAIContract(true);
    setSuccess("Compilazione del template .docx ufficiale in corso...");
    try {
      const isRitenuta = nurse.tipo_contratto?.toLowerCase().includes('ritenuta') || nurse.tipo_contratto?.toLowerCase().includes('occasional');
      const templateName = isRitenuta ? 'template-ritenuta.docx' : 'template-piva.docx';
      
      const res = await fetch(`/${templateName}`);
      if (!res.ok) throw new Error(`Non riesco a trovare il file /${templateName} nel server.`);
      
      const blob = await res.blob();
      const arrayBuffer = await blob.arrayBuffer();

      const zip = new PizZip(arrayBuffer);
      
      // === CRITICAL FIX: Merge fragmented XML runs ===
      // Word splits {tag_name} across multiple <w:r> elements. Docxtemplater can't find them.
      const docXmlFile = zip.file('word/document.xml')
      if (docXmlFile) {
        let docXml = docXmlFile.asText()
        // Remove proofErr tags that Word inserts between { and tag_name
        docXml = docXml.replace(/<w:proofErr[^/]*\/>/g, '')
        // Merge known tags split across runs
        const tagNames = ['nome_cognome','cf','p\\.iva','residenza','pec','data_abilitazione',
          'albo_iscrizione','albo_numero','data_iscrizione','polizza_numero','polizza_compagnia',
          'polizza_massimale','polizza_scadenza','iban','professionista']
        for (const tagName of tagNames) {
          const re = new RegExp(
            '\\{(<\\/w:t>.*?<w:t[^>]*>)*\\s*' + tagName + '\\s*(<\\/w:t>.*?<w:t[^>]*>)*\\}',
            'gs'
          )
          docXml = docXml.replace(re, `{${tagName.replace(/\\\./g, '.')}}`)
        }
        // Generic cleanup for remaining split braces
        docXml = docXml.replace(
          /\{(<\/w:t><\/w:r>(?:<w:r\b[^>]*>(?:<w:rPr>(?:(?!<\/w:rPr>).)*<\/w:rPr>)?<w:t\b[^>]*>)?)([a-z_]+(?:\.[a-z]+)?)((?:<\/w:t><\/w:r>(?:<w:r\b[^>]*>(?:<w:rPr>(?:(?!<\/w:rPr>).)*<\/w:rPr>)?<w:t\b[^>]*>)?)?)\}/gs,
          '{$2}'
        )
        zip.file('word/document.xml', docXml)
      }

      const dataIscrizione = nurse.data_iscrizione_albo || nurse.data_abilitazione || ''
      const dataFormattata = dataIscrizione ? new Date(dataIscrizione).toLocaleDateString('it-IT') : ''
      const dataAbilitazione = nurse.data_abilitazione ? new Date(nurse.data_abilitazione).toLocaleDateString('it-IT') : ''
      const polizzaScadenza = nurse.assicurazione_scadenza ? new Date(nurse.assicurazione_scadenza).toLocaleDateString('it-IT') : ''

      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
      });

      const nomeCognome = `${nurse.nome || ''} ${nurse.cognome || ''}`.trim()
      doc.render({
        nome_cognome: nomeCognome,
        cf: nurse.codice_fiscale || '________________',
        'p.iva': nurse.partita_iva || '________________',
        residenza: nurse.residenza_contratto || nurse.indirizzo || '________________',
        pec: nurse.pec || '________________',
        data_abilitazione: dataAbilitazione || dataFormattata || '________________',
        albo_iscrizione: nurse.opi_provincia || '________________',
        albo_numero: nurse.opi_numero || '________',
        data_iscrizione: dataFormattata || '________________',
        polizza_numero: nurse.assicurazione || '________________',
        polizza_compagnia: nurse.assicurazione || '________________',
        polizza_massimale: nurse.polizza_massimale || '________________',
        polizza_scadenza: polizzaScadenza || '________________',
        iban: nurse.iban || '________________________________',
        professionista: nurse.pec || '________________'
      });

      const out = doc.getZip().generate({
        type: "blob",
        mimeType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      const tipoLabel = isRitenuta ? 'Ritenuta_Acconto' : 'Partita_IVA'
      const today = new Date().toLocaleDateString('it-IT').replace(/\//g, '-')
      saveAs(out, `Contratto_${tipoLabel}_${nurse.cognome}_${nurse.nome}_${today}.docx`);
      setSuccess('Template compilato e scaricato brillantemente in formato Word!');
    } catch (e: any) {
      setError('Errore di compilazione del Contratto: ' + e.message);
    } finally {
      setDraftingAIContract(false);
    }
  }

  // Core Gemini AI extraction
  async function runGeminiExtraction(files: FileList, progressCallback: (msg: string) => void): Promise<Partial<Nurse>> {
    let apiKey = localStorage.getItem('GEMINI_API_KEY');
    if (!apiKey) {
      alert("API Key Gemini mancante! Inseriscila nelle Impostazioni (Settings) per sbloccare l'OCR.");
      throw new Error("L'inserimento dell'API Key è obbligatorio per utilizzare l'A.I. Gemini.");
    }

    progressCallback("Preparazione e caricamento documenti...");
    const imageParts: any[] = [];
    let extraTextFromDocs = "";

    for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.name.endsWith('.doc') || file.name.endsWith('.docx') || file.type.includes('word') || ['txt', 'csv'].includes(file.name.split('.').pop()?.toLowerCase() || '')) {
            const txt = await extractTextFromFile(file, progressCallback);
            extraTextFromDocs += `\n\n--- DOCUMENTO ALLEGATO: ${file.name} ---\n${txt}\n`;
        } else {
            imageParts.push(await fileToGenerativePart(file));
        }
    }

    progressCallback("Analisi AI Generativa in corso (Gemini 2.0)...");
    const promptText = `
    Sei un assistente AI avanzato specializzato in HR per personale sanitario in Italia.
    Analizza TUTTI i documenti forniti (CV, tesserini OPI/IPASVI, documenti di identità, codici fiscali, attestati P.IVA, polizze assicurative, attestati di laurea, contratti) ed estrai TUTTE le informazioni del professionista.
    
    ISTRUZIONI PER TIPO DOCUMENTO:
    - CARTA D'IDENTITÀ / PATENTE: estrai nome, cognome, data di nascita, indirizzo di residenza, codice fiscale se presente
    - CODICE FISCALE: estrai il codice fiscale alfanumerico di 16 caratteri
    - ATTESTATO PARTITA IVA / VISURA CAMERALE: estrai il numero di Partita IVA (11 cifre)
    - TESSERINO OPI / IPASVI / ALBO: estrai provincia dell'ordine, numero iscrizione, data iscrizione, data abilitazione
    - POLIZZA ASSICURATIVA / DICHIARAZIONE DI COPERTURA: estrai compagnia assicurativa, numero polizza, massimale in euro, data scadenza
    - CV / ATTESTATO LAUREA: estrai qualifica professionale, email, telefono, PEC, IBAN
    
    ${extraTextFromDocs}

    Regole ferree:
    - Restituisci SOLO ed ESCLUSIVAMENTE un oggetto JSON valido. Non aggiungere markdown come \`\`\`json.
    - Se un'informazione non è chiaramente deducibile o è incerta, omettila (non inventare).
    
    Il JSON deve rispettare le seguenti chiavi (compila TUTTE quelle che trovi):
    {
      "nome": "string (solo il nome proprio)",
      "cognome": "string (solo il cognome)",
      "codice_fiscale": "string (16 caratteri alfanumerici maiuscoli)",
      "partita_iva": "string (11 cifre, dal documento P.IVA o visura)",
      "telefono": "string (formato numerico senza spazi)",
      "email": "string",
      "pec": "string (indirizzo PEC se trovato)",
      "indirizzo": "string (domicilio: via/piazza e numero civico)",
      "residenza_contratto": "string (residenza fiscale completa con CAP e città)",
      "qualifica": "Infermiere",
      "opi_provincia": "string (provincia dell'OPI/IPASVI)",
      "opi_numero": "string (numero iscrizione albo)",
      "data_abilitazione": "string (formato YYYY-MM-DD)",
      "data_iscrizione_albo": "string (formato YYYY-MM-DD)",
      "assicurazione": "string (nome compagnia assicurativa)",
      "polizza_massimale": "string (massimale in euro, solo cifra es. '2500000')",
      "assicurazione_scadenza": "string (scadenza polizza, formato YYYY-MM-DD)",
      "iban": "string (formato IT...)"
    }`;

    try {
      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{ parts: [{ text: promptText }, ...imageParts] }]
        })
      });

      if (!response.ok) {
        let eMsg = `Errore Server API (${response.status})`; try { const eData = await response.json(); eMsg += `: ${eData.error?.message || JSON.stringify(eData)}`; } catch(e){} throw new Error(eMsg);
      }

      const data = await response.json();
      let aiTextResponse = data.candidates?.[0]?.content?.parts?.[0]?.text || '{}';
      
      aiTextResponse = aiTextResponse.replace(/```json/g, '').replace(/```/g, '').trim();
      const extractedInfo = JSON.parse(aiTextResponse);
      return extractedInfo;

    } catch (err: any) {
      throw new Error(err.message || "Parsing fallito");
    }
  }

  // Nuova logic OCR modale
  async function handleOCRNurseForm(files: FileList) {
    if (files.length === 0) return
    setOcrProcessing(true)
    
    try {
      const extracted = await runGeminiExtraction(files, setOcrProgress);

      const updates: Partial<Nurse> = {};
      const compilati: string[] = [];
      const ignorati: string[] = [];
      const fieldLabels: Record<string, string> = {
        nome: 'Nome', cognome: 'Cognome', telefono: 'Telefono', email: 'Email',
        codice_fiscale: 'Codice Fiscale', partita_iva: 'P.IVA', qualifica: 'Qualifica',
        indirizzo: 'Indirizzo', residenza_contratto: 'Residenza', pec: 'PEC',
        opi_provincia: 'Albo OPI', opi_numero: 'N° Albo', data_abilitazione: 'Data Abilitazione',
        data_iscrizione_albo: 'Data Iscrizione', assicurazione: 'Assicurazione',
        polizza_massimale: 'Massimale', assicurazione_scadenza: 'Scadenza Polizza',
        iban: 'IBAN', regione: 'Regione', provincia: 'Provincia',
      };

      for (const [key, value] of Object.entries(extracted)) {
        if (value && typeof value === 'string' && value.trim() !== '') {
          const label = fieldLabels[key] || key;
          if (!formData[key as keyof Nurse] || formData[key as keyof Nurse] === 'Infermiere') {
            updates[key as keyof Nurse] = value as any;
            compilati.push(`${label}: ${value.length > 30 ? value.substring(0, 30) + '...' : value}`);
          } else {
            ignorati.push(`${label} (già presente)`);
          }
        }
      }

      if (compilati.length > 0) {
        setFormData(prev => ({ ...prev, ...updates }));
        let msg = `✅ AI: ${compilati.length} campi compilati:\n• ${compilati.join('\n• ')}`;
        if (ignorati.length > 0) msg += `\n\n⏭️ ${ignorati.length} campi ignorati:\n• ${ignorati.join('\n• ')}`;
        setSuccess(msg);
      } else {
        const msg = ignorati.length > 0
          ? `AI completata. Nessun nuovo dato aggiunto.\n⏭️ ${ignorati.length} campi ignorati (già presenti):\n• ${ignorati.join('\n• ')}`
          : 'AI completata. Nessun dato utile trovato nei documenti caricati.';
        setSuccess(msg);
      }
    } catch (err: any) {
      setError('Errore AI: ' + (err.message || 'sconosciuto'));
    } finally {
      setOcrProcessing(false);
      setOcrProgress('');
    }
  }

  // Nuova logic OCR esistenti (Update Backend)
  async function handleOCRUpload(files: FileList, nurse: Nurse) {
    if (files.length === 0) return
    setOcrProcessing(true)
    
    try {
      const extracted = await runGeminiExtraction(files, setOcrProgress);

      const updates: Partial<Nurse> = {};
      const compilati: string[] = [];
      const ignorati: string[] = [];
      const fieldLabels: Record<string, string> = {
        nome: 'Nome', cognome: 'Cognome', telefono: 'Telefono', email: 'Email',
        codice_fiscale: 'Codice Fiscale', partita_iva: 'P.IVA', qualifica: 'Qualifica',
        indirizzo: 'Indirizzo', residenza_contratto: 'Residenza', pec: 'PEC',
        opi_provincia: 'Albo OPI', opi_numero: 'N° Albo', data_abilitazione: 'Data Abilitazione',
        data_iscrizione_albo: 'Data Iscrizione', assicurazione: 'Assicurazione',
        polizza_massimale: 'Massimale', assicurazione_scadenza: 'Scadenza Polizza',
        iban: 'IBAN', regione: 'Regione', provincia: 'Provincia',
      };

      for (const [key, value] of Object.entries(extracted)) {
        if (value && typeof value === 'string' && value.trim() !== '') {
          const label = fieldLabels[key] || key;
          if (!nurse[key as keyof Nurse]) {
            updates[key as keyof Nurse] = value as any;
            compilati.push(`${label}: ${value.length > 30 ? value.substring(0, 30) + '...' : value}`);
          } else {
            ignorati.push(`${label} (già presente)`);
          }
        }
      }

      if (compilati.length > 0) {
        setOcrProgress('Salvataggio su cloud...');
        await supabase.from('nurses').update(updates).eq('id', nurse.id);
        logActivity('nurse', nurse.id, 'ai_update', `Gemini A.I. ha compilato: ${compilati.map(c => c.split(':')[0]).join(', ')}`);
        fetchNurses();
        if (selectedNurse?.id === nurse.id) {
          setSelectedNurse(prev => prev ? { ...prev, ...updates } as Nurse : null);
        }
        let msg = `✅ AI: ${compilati.length} campi aggiornati:\n• ${compilati.join('\n• ')}`;
        if (ignorati.length > 0) msg += `\n\n⏭️ ${ignorati.length} campi ignorati:\n• ${ignorati.join('\n• ')}`;
        setSuccess(msg);
      } else {
        const msg = ignorati.length > 0
          ? `AI completata. Nessun nuovo dato aggiunto.\n⏭️ ${ignorati.length} campi ignorati (già presenti):\n• ${ignorati.join('\n• ')}`
          : 'AI completata: nessun dato utile trovato nei documenti.';
        setSuccess(msg);
      }
    } catch (err: any) {
      setError('Errore AI: ' + (err.message || 'sconosciuto'));
    } finally {
      setOcrProcessing(false);
      setOcrProgress('');
    }
  }

  // Activity log helper
  async function logActivity(entityType: string, entityId: string, action: string, details?: string) {
    try {
      await supabase.from('activity_log').insert({
        entity_type: entityType,
        entity_id: entityId,
        action,
        details: details || '',
        created_at: new Date().toISOString(),
      })
    } catch { /* non-blocking */ }
  }

  // KPI calculations
  const kpi = useMemo(() => {
    const totale = nurses.length
    const contrattiAttivi = nurses.filter(n => n.contratto_firmato).length
    const today = new Date()

    let assicurazioniInScadenza = 0
    let assicurazioniScadute = 0
    nurses.forEach(n => {
      if (n.assicurazione_scadenza) {
        const exp = new Date(n.assicurazione_scadenza)
        const diff = Math.ceil((exp.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
        if (diff < 0) assicurazioniScadute++
        else if (diff <= 30) assicurazioniInScadenza++
      }
    })

    // Region count
    const regionCount: Record<string, number> = {}
    Object.keys(regionsData).forEach(r => { regionCount[r] = nurses.filter(n => n.regione === r).length })
    const topRegions = Object.entries(regionCount).sort((a, b) => b[1] - a[1]).filter(([, c]) => c > 0).slice(0, 5)
    const maxRegionCount = Math.max(1, ...Object.values(regionCount))

    // Qualifiche
    const qualificheMap: Record<string, number> = {}
    nurses.forEach(n => {
      const q = n.qualifica || 'Non specificata'
      qualificheMap[q] = (qualificheMap[q] || 0) + 1
    })

    return { totale, contrattiAttivi, assicurazioniInScadenza, assicurazioniScadute, regionCount, topRegions, maxRegionCount, qualificheMap }
  }, [nurses])

  // Filtered nurses
  const filteredNurses = useMemo(() => {
    let list = showArchiviati ? nurses : nurses.filter(n => !n.archiviato)

    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      list = list.filter(n =>
        n.nome.toLowerCase().includes(q) ||
        n.cognome.toLowerCase().includes(q) ||
        (n.codice_fiscale || '').toLowerCase().includes(q)
      )
    } else if (selectedProvince) {
      list = list.filter(n => n.provincia === selectedProvince)
    } else if (selectedRegion) {
      list = list.filter(n => n.regione === selectedRegion)
    }

    // KPI filter
    if (kpiFilter === 'contratti') {
      list = list.filter(n => n.contratto_firmato)
    } else if (kpiFilter === 'assicurazioni') {
      list = list.filter(n => {
        if (!n.assicurazione_scadenza) return true // missing = problematic
        const exp = new Date(n.assicurazione_scadenza)
        const diff = Math.ceil((exp.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
        return diff <= 30
      })
    }

    return list
  }, [nurses, searchQuery, selectedRegion, selectedProvince, kpiFilter])

  // Map color helper
  function getRegColor(region: string) {
    const count = kpi.regionCount[region] || 0
    if (count === 0) return '#e2e8f0'
    const intensity = Math.min(1, count / kpi.maxRegionCount)
    const r = Math.round(224 - intensity * 155)
    const g = Math.round(231 - intensity * 118)
    const b = Math.round(240 - intensity * 42)
    return `rgb(${r},${g},${b})`
  }

  // Insurance status
  function getAssuranceStatus(nurse: Nurse) {
    if (!nurse.assicurazione) return { label: 'No Assicurazione', color: 'red' as const }
    if (!nurse.assicurazione_scadenza) return { label: 'Scadenza mancante', color: 'amber' as const }
    const exp = new Date(nurse.assicurazione_scadenza)
    const diff = Math.ceil((exp.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
    if (diff < 0) return { label: 'Scaduta', color: 'red' as const }
    if (diff <= 30) return { label: `Scade tra ${diff} gg`, color: 'amber' as const }
    return { label: 'Attiva', color: 'green' as const }
  }

  // Navigation helpers
  function goHome() { setViewMode('regions'); setSelectedRegion(null); setSelectedProvince(null); setKpiFilter(null) }
  function goBack() {
    if (kpiFilter) { setKpiFilter(null); setViewMode('regions') }
    else if (selectedProvince) { setSelectedProvince(null); setViewMode('provinces') }
    else if (selectedRegion) { setSelectedRegion(null); setViewMode('regions') }
  }

  // Nurse CRUD
  function handleOpenForm(nurse?: Nurse) {
    if (nurse) {
      setEditingNurse(nurse)
      setFormData({ ...nurse })
    } else {
      setEditingNurse(null)
      setFormData({
        ...emptyForm,
        regione: selectedRegion || '',
        provincia: selectedProvince || '',
      })
    }
    setShowNurseForm(true)
  }

  async function handleSaveNurse() {
    if (!formData.nome || !formData.cognome || !formData.regione || !formData.provincia) {
      setError('Nome, cognome, regione e provincia sono obbligatori')
      return
    }
    setSaving(true)
    setError('')

    try {
      if (editingNurse) {
        const { error: err } = await supabase
          .from('nurses')
          .update(formData)
          .eq('id', editingNurse.id)
        if (err) throw err
        logActivity('nurse', editingNurse.id, 'update', `Aggiornato ${formData.cognome} ${formData.nome}`)
        setSuccess('Infermiere aggiornato!')
      } else {
        const { data: inserted, error: err } = await supabase
          .from('nurses')
          .insert(formData)
          .select('id')
          .single()
        if (err) throw err
        if (inserted) logActivity('nurse', inserted.id, 'create', `Creato ${formData.cognome} ${formData.nome}`)
        setSuccess('Infermiere aggiunto!')
      }
      setShowNurseForm(false)
      fetchNurses()
    } catch (err: any) {
      setError(err.message || 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  async function handleDeleteNurse(id: string, name: string) {
    if (!confirm(`Eliminare ${name}?`)) return
    const { error: err } = await supabase.from('nurses').delete().eq('id', id)
    if (err) {
      setError('Errore eliminazione: ' + err.message)
    } else {
      logActivity('nurse', id, 'delete', `Eliminato ${name}`)
      setSuccess('Infermiere eliminato')
      fetchNurses()
    }
  }

  // --- EXPORT FUNCTIONS ---
  function getExportTitle() {
    if (searchQuery) return `Ricerca: "${searchQuery}"`
    if (selectedProvince) return `Provincia di ${selectedProvince}`
    if (selectedRegion) return `Regione ${selectedRegion}`
    return 'Tutti i Professionisti'
  }

  // === GEO-SEARCH: Find nearest operators ===
  function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371
    const dLat = (lat2 - lat1) * Math.PI / 180
    const dLon = (lon2 - lon1) * Math.PI / 180
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  }

  async function geocodeAddress(address: string): Promise<{ lat: number; lon: number } | null> {
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&countrycodes=it&limit=1`, {
        headers: { 'User-Agent': 'QuiCare-GestioneInfermieri/1.0' }
      })
      const data = await res.json()
      if (data.length > 0) return { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) }
      return null
    } catch { return null }
  }

  async function handleGeoSearch() {
    if (!geoSearchAddress.trim()) return
    setGeoSearching(true)
    setGeoError('')
    setGeoResults([])

    // Geocode the target address
    const target = await geocodeAddress(geoSearchAddress)
    if (!target) {
      setGeoError('Indirizzo non trovato. Prova con un indirizzo più specifico.')
      setGeoSearching(false)
      return
    }

    // For each nurse, geocode their location and calculate distance
    const results: { nurse: Nurse; distanceKm: number; inRange: boolean; zoneMatch: boolean }[] = []

    for (const nurse of nurses) {
      // Build address from available fields
      const nurseAddr = [nurse.indirizzo, nurse.provincia, nurse.regione].filter(Boolean).join(', ')
      if (!nurseAddr) continue

      const nurseCoord = await geocodeAddress(nurseAddr)
      if (!nurseCoord) continue

      const distanceKm = haversineDistance(target.lat, target.lon, nurseCoord.lat, nurseCoord.lon)

      // Check if in range
      const maxKm = nurse.km_spostamento ? parseFloat(nurse.km_spostamento) : 999
      const inRange = distanceKm <= maxKm

      // Check zone match
      const zoneMatch = nurse.zone_coperte
        ? geoSearchAddress.toLowerCase().split(/[,\s]+/).some(word =>
            word.length > 2 && nurse.zone_coperte!.toLowerCase().includes(word)
          )
        : false

      results.push({ nurse, distanceKm: Math.round(distanceKm * 10) / 10, inRange, zoneMatch })

      // Rate limit Nominatim (1 req/sec)
      await new Promise(r => setTimeout(r, 1100))
    }

    // Sort by distance
    results.sort((a, b) => a.distanceKm - b.distanceKm)
    setGeoResults(results)
    setGeoSearching(false)
  }

  // --- A.I. Executive Summary Generator ---
  async function generateExportSummary(nurses: Nurse[], type: string): Promise<string> {
    let apiKey = localStorage.getItem('GEMINI_API_KEY');
    if (!apiKey) return "Sommario A.I. non disponibile (API Key mancante).";
    
    const leanData = nurses.map(n => ({
      reg: n.regione,
      prov: n.provincia,
      contr: n.contratto_firmato ? 'Si' : 'No',
      ass_scad: n.assicurazione_scadenza || 'N/A'
    }));
    
    try {
      const promptText = `Sei l'AI direzionale HR. Analizza questo array di infermieri. 
Scrivi un "Executive Summary" formale e professionale (circa 120 parole) descrivendo lo stato attuale, le regioni con  più personale, una percentuale stimata di contratti attivi e quante eventuali allerte ci sono per assicurazioni scadute/assenti. Non usare markdown o asterischi, usa testo discorsivo semplice idoneo per un PDF aziendale.
Dati: ${JSON.stringify(leanData)}`;
      
      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contents: [{ parts: [{ text: promptText }] }] })
      });
      if (!response.ok) return "Errore A.I.: Impossibile generare il sommario.";
      const data = await response.json();
      let aiText = data.candidates?.[0]?.content?.parts?.[0]?.text || "Nessun output.";
      return aiText.replace(/\*/g, '').trim();
    } catch {
      return "Errore A.I. interno.";
    }
  }

  async function handleExportExcel() {
    setSuccess('Esecuzione analisi A.I. e preparazione export Excel in corso, attendere...');
    const title = getExportTitle()
    const today = new Date().toLocaleDateString('it-IT')

    const sorted = [...filteredNurses].sort((a, b) => {
      if (a.regione !== b.regione) return a.regione.localeCompare(b.regione)
      if (a.provincia !== b.provincia) return a.provincia.localeCompare(b.provincia)
      return a.cognome.localeCompare(b.cognome)
    })

    const wb = new ExcelJS.Workbook()
    wb.creator = 'QuiCare Srl'
    wb.created = new Date()

    // === HELPER: apply style to every cell in a row (ExcelJS needs cell-level styling) ===
    function styleRow(ws: any, rowNum: number, colCount: number, styles: { font?: any; fill?: any; alignment?: any; border?: any }) {
      const row = ws.getRow(rowNum)
      for (let c = 1; c <= colCount; c++) {
        const cell = row.getCell(c)
        if (styles.font) cell.font = styles.font
        if (styles.fill) cell.fill = styles.fill
        if (styles.alignment) cell.alignment = styles.alignment
        if (styles.border) cell.border = styles.border
      }
    }

    // Shared border styles
    const thinBorder = {
      top: { style: 'thin' as const, color: { argb: 'FFD1D5DB' } },
      left: { style: 'thin' as const, color: { argb: 'FFD1D5DB' } },
      bottom: { style: 'thin' as const, color: { argb: 'FFD1D5DB' } },
      right: { style: 'thin' as const, color: { argb: 'FFD1D5DB' } },
    }
    const thickBorderBottom = {
      ...thinBorder,
      bottom: { style: 'medium' as const, color: { argb: 'FF1E3A5F' } },
    }

    // Stats
    const contrattiAttivi = sorted.filter(n => n.contratto_firmato).length
    const regioniSet = new Set(sorted.map(n => n.regione))
    const regioniCoperte = regioniSet.size
    const regionCount: Record<string, number> = {}
    sorted.forEach(n => { regionCount[n.regione] = (regionCount[n.regione] || 0) + 1 })
    const provinceCount: Record<string, number> = {}
    sorted.forEach(n => { provinceCount[n.provincia] = (provinceCount[n.provincia] || 0) + 1 })
    const assScadute = sorted.filter(n => {
      if (!n.assicurazione_scadenza) return false
      return new Date(n.assicurazione_scadenza) < new Date()
    }).length

    // ══════════════════════════════════════════════════════════════
    // FOGLIO 1: RESOCONTO (Summary) — Clean professional layout
    // ══════════════════════════════════════════════════════════════
    const wsSummary = wb.addWorksheet('Resoconto', { properties: { tabColor: { argb: 'FF1E293B' } } })
    wsSummary.columns = [
      { width: 4 }, { width: 32 }, { width: 18 }, { width: 18 }, { width: 18 }, { width: 18 }, { width: 18 }, { width: 4 }
    ]

    // Corporate colors — single palette
    const CORP = {
      dark: 'FF1E293B',      // Header background
      medium: 'FF334155',    // Sub-header
      accent: 'FF475569',    // Section titles
      light: 'FFF1F5F9',     // Alternating rows
      white: 'FFFFFFFF',
      border: 'FFE2E8F0',
      textDark: 'FF1E293B',
      textMedium: 'FF475569',
      textLight: 'FF94A3B8',
      green: 'FF059669',
      red: 'FFDC2626',
    }

    // Row 1: Header banner
    wsSummary.mergeCells('A1:H1')
    wsSummary.getRow(1).height = 45
    const bannerCell = wsSummary.getRow(1).getCell(1)
    bannerCell.value = 'QuiCare Srl — Archivio Professionisti'
    bannerCell.font = { size: 20, bold: true, color: { argb: CORP.white } }
    bannerCell.alignment = { vertical: 'middle', horizontal: 'center' }
    styleRow(wsSummary, 1, 8, { fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: CORP.dark } } })

    // Row 2: Info ribbon
    wsSummary.mergeCells('A2:H2')
    wsSummary.getRow(2).height = 26
    const infoCell2 = wsSummary.getRow(2).getCell(1)
    infoCell2.value = `${title}  ·  Report generato il ${today}  ·  ${sorted.length} professionisti  ·  ${contrattiAttivi} contratti attivi  ·  ${regioniCoperte} regioni`
    infoCell2.font = { size: 10, color: { argb: CORP.textMedium } }
    infoCell2.alignment = { vertical: 'middle', horizontal: 'center' }
    styleRow(wsSummary, 2, 8, { fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: CORP.light } } })

    // Row 3: spacer
    wsSummary.getRow(3).height = 10

    // Row 4: Stats header
    wsSummary.mergeCells('B4:G4')
    wsSummary.getRow(4).height = 28
    const statsTitle = wsSummary.getRow(4).getCell(2)
    statsTitle.value = 'RIEPILOGO GENERALE'
    statsTitle.font = { size: 12, bold: true, color: { argb: CORP.white } }
    statsTitle.alignment = { vertical: 'middle', horizontal: 'left' }
    for (let c = 2; c <= 7; c++) {
      wsSummary.getRow(4).getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: CORP.dark } }
      wsSummary.getRow(4).getCell(c).font = statsTitle.font
    }

    // Stats rows (simple label: value pairs)
    const stats = [
      ['Totale Professionisti', sorted.length],
      ['Contratti Firmati', `${contrattiAttivi} su ${sorted.length} (${sorted.length > 0 ? Math.round((contrattiAttivi / sorted.length) * 100) : 0}%)`],
      ['Regioni Coperte', regioniCoperte],
      ['Province Coperte', Object.keys(provinceCount).length],
      ['Assicurazioni Scadute', assScadute > 0 ? `⚠ ${assScadute}` : '0'],
    ]

    let sr = 5
    stats.forEach(([label, value], idx) => {
      const row = wsSummary.getRow(sr)
      row.height = 24
      const bgColor = idx % 2 === 0 ? CORP.light : CORP.white
      row.getCell(2).value = label
      row.getCell(2).font = { size: 10, bold: true, color: { argb: CORP.textDark } }
      row.getCell(2).alignment = { vertical: 'middle', horizontal: 'left' }
      row.getCell(5).value = value
      row.getCell(5).font = { size: 10, bold: true, color: { argb: CORP.textDark } }
      row.getCell(5).alignment = { vertical: 'middle', horizontal: 'left' }
      for (let c = 2; c <= 7; c++) {
        row.getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } }
        row.getCell(c).border = { bottom: { style: 'thin' as const, color: { argb: CORP.border } } }
      }
      sr++
    })

    // Spacer
    sr++

    // Regional breakdown table — clean, neutral
    const regTableStart = sr
    wsSummary.mergeCells(`B${regTableStart}:G${regTableStart}`)
    wsSummary.getRow(regTableStart).height = 28
    const regTitle = wsSummary.getRow(regTableStart).getCell(2)
    regTitle.value = 'DISTRIBUZIONE PER REGIONE'
    regTitle.font = { size: 12, bold: true, color: { argb: CORP.white } }
    regTitle.alignment = { vertical: 'middle', horizontal: 'left' }
    for (let c = 2; c <= 7; c++) {
      wsSummary.getRow(regTableStart).getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: CORP.dark } }
      wsSummary.getRow(regTableStart).getCell(c).font = regTitle.font
    }

    const regHeaderRow = regTableStart + 1
    wsSummary.getRow(regHeaderRow).height = 24
    const regHeaders = ['', 'Regione', 'Professionisti', 'Contratti Attivi', 'Province', '% su Totale']
    regHeaders.forEach((h, i) => {
      const cell = wsSummary.getRow(regHeaderRow).getCell(i + 2)
      cell.value = h
      cell.font = { size: 10, bold: true, color: { argb: CORP.white } }
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: CORP.medium } }
      cell.alignment = { vertical: 'middle', horizontal: i >= 2 ? 'center' : 'left' }
    })

    const sortedRegions = Object.entries(regionCount).sort((a, b) => b[1] - a[1])
    let regRow = regHeaderRow + 1
    sortedRegions.forEach(([region, count], idx) => {
      const row = wsSummary.getRow(regRow)
      row.height = 22
      const regProvince = [...new Set(sorted.filter(n => n.regione === region).map(n => n.provincia))].length
      const regContratti = sorted.filter(n => n.regione === region && n.contratto_firmato).length
      const percentage = sorted.length > 0 ? `${Math.round((count / sorted.length) * 100)}%` : '0%'

      const bgColor = idx % 2 === 0 ? CORP.light : CORP.white
      const values = ['', region, count, regContratti, regProvince, percentage]
      values.forEach((v, i) => {
        const cell = row.getCell(i + 2)
        cell.value = v
        cell.font = i === 1 ? { size: 10, bold: true, color: { argb: CORP.textDark } } : { size: 10, color: { argb: CORP.textMedium } }
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } }
        cell.alignment = { vertical: 'middle', horizontal: i >= 2 ? 'center' : 'left' }
        cell.border = { bottom: { style: 'thin' as const, color: { argb: CORP.border } } }
      })
      regRow++
    })

    // Totals row
    const totRow = wsSummary.getRow(regRow)
    totRow.height = 26
    const totValues = ['', 'TOTALE', sorted.length, contrattiAttivi, Object.keys(provinceCount).length, '100%']
    totValues.forEach((v, i) => {
      const cell = totRow.getCell(i + 2)
      cell.value = v
      cell.font = { size: 11, bold: true, color: { argb: CORP.white } }
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: CORP.dark } }
      cell.alignment = { vertical: 'middle', horizontal: i >= 2 ? 'center' : 'left' }
    })

    // Footer
    regRow += 2
    wsSummary.mergeCells(`B${regRow + 2}:G${regRow + 2}`)
    const footerCell = wsSummary.getRow(regRow + 2).getCell(2)
    footerCell.value = `QuiCare Srl — Documento riservato — Generato automaticamente il ${today}`
    footerCell.font = { size: 8, italic: true, color: { argb: CORP.textLight } }
    footerCell.alignment = { horizontal: 'center' }

    wsSummary.pageSetup = { orientation: 'landscape', fitToPage: true, fitToWidth: 1 }

    // ══════════════════════════════════════════════════════════════
    // FOGLIO 2: ARCHIVIO NAZIONALE (Data) — Clean single-color layout
    // ══════════════════════════════════════════════════════════════
    const wsMain = wb.addWorksheet('Archivio Nazionale', { properties: { tabColor: { argb: CORP.dark } } })
    const colCount = 24

    // Set column widths
    const colWidths = [5, 22, 20, 16, 18, 18, 18, 28, 22, 22, 18, 18, 12, 26, 20, 20, 28, 22, 26, 16, 14, 26, 16, 35]
    colWidths.forEach((w, i) => { wsMain.getColumn(i + 1).width = w })

    // Row 1: Title banner
    wsMain.mergeCells('A1:X1')
    wsMain.getRow(1).height = 38
    const titleCell = wsMain.getRow(1).getCell(1)
    titleCell.value = `ARCHIVIO NAZIONALE PROFESSIONISTI — QuiCare Srl`
    titleCell.font = { size: 16, bold: true, color: { argb: CORP.white } }
    titleCell.alignment = { vertical: 'middle', horizontal: 'center' }
    styleRow(wsMain, 1, colCount, {
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: CORP.dark } },
    })

    // Row 2: Info ribbon
    wsMain.mergeCells('A2:X2')
    wsMain.getRow(2).height = 24
    const infoCell = wsMain.getRow(2).getCell(1)
    infoCell.value = `${title}  ·  ${sorted.length} professionisti  ·  ${contrattiAttivi} contratti attivi  ·  ${regioniCoperte} regioni  ·  Aggiornato al ${today}`
    infoCell.font = { size: 10, color: { argb: CORP.textMedium } }
    infoCell.alignment = { vertical: 'middle', horizontal: 'center' }
    styleRow(wsMain, 2, colCount, {
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: CORP.light } },
    })

    // Row 3: Column headers
    const headers = ['#', 'Cognome', 'Nome', 'Qualifica', 'Regione', 'Provincia', 'Telefono', 'Email', 'P.IVA', 'Codice Fiscale', 'Tipo Contratto', 'Stato Contratto', 'Raggio Km', 'Zone Coperte', 'Disponibilità', 'Fasce Orarie', 'Indirizzo', 'Residenza', 'PEC', 'Albo OPI', 'N° Albo', 'Assicurazione', 'Scadenza Ass.', 'Note']
    wsMain.getRow(3).height = 26
    headers.forEach((h, i) => {
      const cell = wsMain.getRow(3).getCell(i + 1)
      cell.value = h
      cell.font = { size: 9, bold: true, color: { argb: CORP.white } }
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: CORP.medium } }
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true }
    })

    // Data rows — GROUPED BY REGION with simple gray separators
    const mainRegions = [...new Set(sorted.map(n => n.regione))].sort()
    let cr = 4
    let globalIdx = 0

    mainRegions.forEach((region) => {
      const regNursesMain = sorted.filter(n => n.regione === region)

      // Simple region separator row (clean gray, no color)
      wsMain.mergeCells(`A${cr}:X${cr}`)
      wsMain.getRow(cr).height = 24
      const regBannerCell = wsMain.getRow(cr).getCell(1)
      regBannerCell.value = `${region.toUpperCase()} — ${regNursesMain.length} professionisti`
      regBannerCell.font = { size: 10, bold: true, color: { argb: CORP.textDark } }
      regBannerCell.alignment = { vertical: 'middle', horizontal: 'left' }
      styleRow(wsMain, cr, colCount, {
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: CORP.light } },
        font: { size: 10, bold: true, color: { argb: CORP.textDark } },
        border: { bottom: { style: 'thin' as const, color: { argb: CORP.border } } },
      })
      regBannerCell.font = { size: 10, bold: true, color: { argb: CORP.textDark } }
      cr++

      // Data rows for this region
      regNursesMain.forEach((n, i) => {
        globalIdx++
        const row = wsMain.getRow(cr)
        const values = [
          globalIdx, n.cognome, n.nome, n.qualifica || '', n.regione, n.provincia,
          n.telefono || '', n.email || '', n.partita_iva || '', n.codice_fiscale || '',
          n.tipo_contratto || n.contratto || '',
          n.contratto_firmato ? '✔ Firmato' : '✖ In attesa', n.km_spostamento || '',
          n.zone_coperte || '', n.disponibilita || '', n.fasce_orarie || '',
          n.indirizzo || '', n.residenza_contratto || '', n.pec || '', n.opi_provincia || '',
          n.opi_numero || '', n.assicurazione || '', n.assicurazione_scadenza || '', n.note || ''
        ]

        const isEven = i % 2 === 0
        const bgColor = isEven ? CORP.light : CORP.white

        values.forEach((v, j) => {
          const cell = row.getCell(j + 1)
          cell.value = v
          cell.font = j === 1 ? { size: 10, bold: true, color: { argb: CORP.textDark } } : { size: 9.5, color: { argb: CORP.textMedium } }
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } }
          cell.alignment = { vertical: 'middle', wrapText: true, horizontal: j === 0 ? 'center' : 'left' }
          cell.border = {
            top: { style: 'thin' as const, color: { argb: CORP.border } },
            left: { style: 'thin' as const, color: { argb: CORP.border } },
            bottom: { style: 'thin' as const, color: { argb: CORP.border } },
            right: { style: 'thin' as const, color: { argb: CORP.border } },
          }

          // Color the status column
          if (j === 11) {
            if (n.contratto_firmato) {
              cell.font = { size: 10, bold: true, color: { argb: CORP.green } }
            } else {
              cell.font = { size: 9, bold: true, color: { argb: CORP.red } }
            }
            cell.alignment = { vertical: 'middle', horizontal: 'center' }
          }
        })
        row.height = 22
        cr++
      })
    })

    // Totals row
    const totalRow = wsMain.getRow(cr)
    totalRow.height = 28
    for (let c = 1; c <= colCount; c++) {
      const cell = totalRow.getCell(c)
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E1B4B' } }
      cell.font = { size: 10, bold: true, color: { argb: 'FFFFFFFF' } }
      cell.alignment = { vertical: 'middle', horizontal: 'center' }
      cell.border = { top: { style: 'medium', color: { argb: 'FF1E1B4B' } } }
    }
    totalRow.getCell(1).value = ''
    totalRow.getCell(2).value = `TOTALE: ${sorted.length} professionisti`
    totalRow.getCell(2).alignment = { vertical: 'middle', horizontal: 'left' }
    totalRow.getCell(12).value = `${contrattiAttivi} firmati`

    // Freeze panes
    wsMain.views = [{ state: 'frozen', xSplit: 3, ySplit: 3 }]
    wsMain.pageSetup = { orientation: 'landscape', fitToPage: true, fitToWidth: 1 }

    // ══════════════════════════════════════════════════════════════
    // FOGLI REGIONALI — Clean single-color design
    // ══════════════════════════════════════════════════════════════
    const regions = [...new Set(sorted.map(n => n.regione))].sort()
    const regionColors: Record<string, { banner: string; header: string; stripe: string; stripeAlt: string; borderColor: string }> = {}
    // Single corporate color for all regional sheets
    const corpRegionalColor = { banner: CORP.dark, header: CORP.medium, stripe: CORP.light, stripeAlt: CORP.white, borderColor: CORP.border }
    regions.forEach((r) => { regionColors[r] = corpRegionalColor })

    regions.forEach(region => {
      const regNurses = sorted.filter(n => n.regione === region)
      if (regNurses.length === 0) return

      const sheetName = region.length > 31 ? region.slice(0, 31) : region
      const colors = regionColors[region]
      const emoji = '📍'
      const wsReg = wb.addWorksheet(sheetName, { properties: { tabColor: { argb: colors.banner } } })

      const regColCount = 16
      const regColWidths = [5, 22, 20, 16, 18, 18, 28, 18, 12, 26, 20, 20, 28, 30, 14, 35]
      regColWidths.forEach((w, i) => { wsReg.getColumn(i + 1).width = w })

      // Row 1: Region banner
      wsReg.mergeCells(`A1:P1`)
      wsReg.getRow(1).height = 38
      const regBanner = wsReg.getRow(1).getCell(1)
      regBanner.value = `${emoji}  ${region.toUpperCase()} — ${regNurses.length} professionisti`
      regBanner.font = { size: 16, bold: true, color: { argb: 'FFFFFFFF' } }
      regBanner.alignment = { vertical: 'middle', horizontal: 'center' }
      styleRow(wsReg, 1, regColCount, {
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: colors.banner } },
      })

      // Row 2: Province summary
      const provList = [...new Set(regNurses.map(n => n.provincia))].sort()
      const provSummary = provList.map(p => `${p}: ${regNurses.filter(n => n.provincia === p).length}`).join('  ·  ')
      wsReg.mergeCells(`A2:P2`)
      wsReg.getRow(2).height = 22
      const provCell = wsReg.getRow(2).getCell(1)
      provCell.value = `Province: ${provSummary}  |  Contratti: ${regNurses.filter(n => n.contratto_firmato).length}/${regNurses.length}`
      provCell.font = { size: 9, italic: true, color: { argb: colors.header } }
      provCell.alignment = { vertical: 'middle', horizontal: 'center' }
      styleRow(wsReg, 2, regColCount, {
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: colors.stripeAlt } },
      })

      // Row 3: Column headers (without "Provincia" since we group by it)
      const regHeaders = ['#', 'Cognome', 'Nome', 'Qualifica', 'Telefono', 'Email', 'Tipo Contratto', 'Stato', 'Raggio Km', 'Zone Coperte', 'Disponibilità', 'Fasce Orarie', 'Progetti Attivi', 'Formazione', 'Data Form.', 'Note']
      wsReg.getRow(3).height = 26
      regHeaders.forEach((h, i) => {
        const cell = wsReg.getRow(3).getCell(i + 1)
        cell.value = h
        cell.font = { size: 10, bold: true, color: { argb: 'FFFFFFFF' } }
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: colors.header } }
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true }
        cell.border = { bottom: { style: 'medium', color: { argb: colors.banner } } }
      })

      // Province — same clean corporate colors
      const provColorSets = [
        { banner: CORP.medium, stripe: CORP.light, stripeAlt: CORP.white, text: CORP.textDark, border: CORP.border, subtotal: CORP.light },
      ]

      // Data rows — GROUPED BY PROVINCE with alternating colors
      const provinces = [...new Set(regNurses.map(n => n.provincia))].sort()
      let rcr = 4
      let globalIdx = 0

      provinces.forEach((provincia, provIdx) => {
        const provNurses = regNurses.filter(n => n.provincia === provincia)
        const pc = provColorSets[provIdx % provColorSets.length]

        // Province sub-header banner (solid colored)
        wsReg.mergeCells(`A${rcr}:P${rcr}`)
        wsReg.getRow(rcr).height = 28
        const provBanner = wsReg.getRow(rcr).getCell(1)
        provBanner.value = `📍  ${provincia.toUpperCase()}  —  ${provNurses.length} professionisti`
        provBanner.font = { size: 12, bold: true, color: { argb: 'FFFFFFFF' } }
        provBanner.alignment = { vertical: 'middle', horizontal: 'left' }
        styleRow(wsReg, rcr, regColCount, {
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: pc.banner } },
          font: { size: 12, bold: true, color: { argb: 'FFFFFFFF' } },
        })
        provBanner.font = { size: 12, bold: true, color: { argb: 'FFFFFFFF' } }
        rcr++

        // Nurses in this province
        provNurses.forEach((n, i) => {
          globalIdx++
          const row = wsReg.getRow(rcr)
          row.height = 22
          const values = [
            globalIdx, n.cognome, n.nome, n.qualifica || '',
            n.telefono || '', n.email || '', n.tipo_contratto || n.contratto || '',
            n.contratto_firmato ? '✔' : '✖', n.km_spostamento || '',
            n.zone_coperte || '', n.disponibilita || '',
            n.fasce_orarie || '',
            (n.progetti_attivi || []).join(', '),
            [n.formazione_fv ? 'FV ✔' : 'FV ✖', n.formazione_qualita ? 'Qual. ✔' : 'Qual. ✖', n.formazione_farmaco ? 'Farm. ✔' : 'Farm. ✖', n.formazione_dispositivo ? 'Disp. ✔' : 'Disp. ✖'].join(' / '),
            n.data_formazione || '',
            n.note || ''
          ]

          const isEven = i % 2 === 0
          const bg = isEven ? pc.stripe : pc.stripeAlt

          values.forEach((v, j) => {
            const cell = row.getCell(j + 1)
            cell.value = v
            cell.font = j === 1
              ? { size: 10, bold: true, color: { argb: 'FF1E293B' } }
              : { size: 9.5, color: { argb: 'FF475569' } }
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } }
            cell.alignment = { vertical: 'middle', wrapText: true, horizontal: j === 0 ? 'center' : 'left' }
            cell.border = {
              top: { style: 'thin', color: { argb: pc.border } },
              left: { style: 'thin', color: { argb: pc.border } },
              bottom: { style: 'thin', color: { argb: pc.border } },
              right: { style: 'thin', color: { argb: pc.border } },
            }

            // Color status column
            if (j === 7) {
              if (n.contratto_firmato) {
                cell.font = { size: 10, bold: true, color: { argb: 'FF047857' } }
              } else {
                cell.font = { size: 9, bold: true, color: { argb: 'FFDC2626' } }
              }
              cell.alignment = { vertical: 'middle', horizontal: 'center' }
            }
          })
          rcr++
        })

        // Province subtotal row
        const provTot = wsReg.getRow(rcr)
        provTot.height = 22
        for (let c = 1; c <= regColCount; c++) {
          const cell = provTot.getCell(c)
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: pc.subtotal } }
          cell.font = { size: 9, bold: true, color: { argb: pc.text } }
          cell.alignment = { vertical: 'middle', horizontal: 'center' }
          cell.border = {
            top: { style: 'thin', color: { argb: pc.border } },
            bottom: { style: 'medium', color: { argb: pc.banner } },
          }
        }
        provTot.getCell(2).value = `Subtotale ${provincia}`
        provTot.getCell(2).alignment = { vertical: 'middle', horizontal: 'left' }
        provTot.getCell(3).value = provNurses.length
        const provContratti = provNurses.filter(n => n.contratto_firmato).length
        provTot.getCell(8).value = `${provContratti}/${provNurses.length}`
        rcr++

        // Spacer between provinces (except after last)
        if (provIdx < provinces.length - 1) {
          wsReg.getRow(rcr).height = 8
          rcr++
        }
      })

      // Region totals row
      const regTot = wsReg.getRow(rcr)
      regTot.height = 28
      for (let c = 1; c <= regColCount; c++) {
        const cell = regTot.getCell(c)
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: colors.header } }
        cell.font = { size: 10, bold: true, color: { argb: 'FFFFFFFF' } }
        cell.alignment = { vertical: 'middle', horizontal: 'center' }
        cell.border = { top: { style: 'medium', color: { argb: colors.banner } } }
      }
      regTot.getCell(2).value = `TOTALE ${region.toUpperCase()}`
      regTot.getCell(2).alignment = { vertical: 'middle', horizontal: 'left' }
      regTot.getCell(3).value = `${regNurses.length} professionisti`
      regTot.getCell(3).alignment = { vertical: 'middle', horizontal: 'left' }
      regTot.getCell(8).value = `${regNurses.filter(n => n.contratto_firmato).length}/${regNurses.length}`

      // Freeze panes
      wsReg.views = [{ state: 'frozen', xSplit: 0, ySplit: 3 }]
      wsReg.pageSetup = { orientation: 'landscape', fitToPage: true, fitToWidth: 1 }
    })

    const buf = await wb.xlsx.writeBuffer()
    saveAs(new Blob([buf]), `QuiCare_Archivio_Nazionale_${new Date().toISOString().slice(0, 10)}.xlsx`)
    setSuccess('✅ Excel esportato — Resoconto + Archivio Nazionale + fogli regionali!')
  }

  async function handleExportPDF() {
    setSuccess('Analisi A.I. per Export PDF in corso...');
    const doc: any = new jsPDF({ orientation: 'landscape' })
    const title = getExportTitle()
    const today = new Date().toLocaleDateString('it-IT')
    const pageW = doc.internal.pageSize.width
    const pageH = doc.internal.pageSize.height

    // === HEADER ===
    // Background gradient effect (two overlapping rects)
    doc.setFillColor(49, 46, 129) // indigo-900
    doc.rect(0, 0, pageW, 32, 'F')
    doc.setFillColor(79, 70, 229) // indigo-600
    doc.rect(0, 0, pageW * 0.65, 32, 'F')

    // Title
    doc.setTextColor(255, 255, 255)
    doc.setFontSize(18)
    doc.setFont('helvetica', 'bold')
    doc.text('GESTIONE INFERMIERI', 14, 14)

    // Subtitle
    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')
    doc.text(`${title}  |  ${filteredNurses.length} professionisti`, 14, 22)

    // Right side branding
    doc.setFontSize(10)
    doc.setFont('helvetica', 'bold')
    doc.text('QuiCare Srl', pageW - 14, 12, { align: 'right' })
    doc.setFontSize(8)
    doc.setFont('helvetica', 'normal')
    doc.text(`Report generato il ${today}`, pageW - 14, 20, { align: 'right' })

    // === KPI BAR ===
    const kpiY = 36
    const kpiBoxW = (pageW - 28 - 12) / 4
    const kpiData = [
      { label: 'Totale', value: `${filteredNurses.length}`, color: [79, 70, 229] },
      { label: 'Contratti Attivi', value: `${filteredNurses.filter(n => n.contratto_firmato).length}`, color: [16, 185, 129] },
      { label: 'Assicurazioni Scadute', value: `${filteredNurses.filter(n => { if (!n.assicurazione_scadenza) return false; return new Date(n.assicurazione_scadenza) < new Date() }).length}`, color: [239, 68, 68] },
      { label: 'Regioni Coperte', value: `${new Set(filteredNurses.map(n => n.regione)).size}`, color: [139, 92, 246] },
    ]

    kpiData.forEach((kpi, i) => {
      const x = 14 + i * (kpiBoxW + 4)
      doc.setFillColor(248, 250, 252)
      doc.roundedRect(x, kpiY, kpiBoxW, 16, 2, 2, 'F')
      doc.setFillColor(kpi.color[0], kpi.color[1], kpi.color[2])
      doc.roundedRect(x, kpiY, 3, 16, 1, 1, 'F')
      doc.setTextColor(kpi.color[0], kpi.color[1], kpi.color[2])
      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.text(kpi.value, x + 8, kpiY + 8)
      doc.setTextColor(100, 116, 139)
      doc.setFontSize(7)
      doc.text(kpi.label, x + 8, kpiY + 13)
    })

    // === AI EXECUTIVE SUMMARY ===
    const aiSummaryText = await generateExportSummary(filteredNurses, 'PDF');
    const aiY = kpiY + 20;
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.5);
    doc.roundedRect(14, aiY, pageW - 28, 22, 2, 2, 'FD'); // Fill and border
    
    doc.setTextColor(79, 70, 229);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('✨ EXECUTIVE SUMMARY A.I.', 18, aiY + 6);
    
    doc.setTextColor(71, 85, 105);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.text(aiSummaryText, 18, aiY + 11, { maxWidth: pageW - 36, lineHeightFactor: 1.4 });

    // === TABLE ===
    doc.setTextColor(0, 0, 0)
    const tableData = filteredNurses.map((n, i) => [
      i + 1,
      `${n.cognome} ${n.nome}`,
      n.qualifica || '',
      `${n.regione}\n${n.provincia}`,
      n.telefono || '',
      n.email || '',
      n.tipo_contratto || n.contratto || '',
      n.contratto_firmato ? '✓' : '—',
    ])

    autoTable(doc, {
      startY: aiY + 26,
      head: [['#', 'Nome Completo', 'Qualifica', 'Regione / Provincia', 'Telefono', 'Email', 'Contratto', 'Stato']],
      body: tableData,
      styles: {
        fontSize: 7.5,
        cellPadding: { top: 2, right: 3, bottom: 2, left: 3 },
        lineColor: [226, 232, 240],
        lineWidth: 0.2,
      },
      headStyles: {
        fillColor: [79, 70, 229],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 7,
        cellPadding: { top: 3, right: 3, bottom: 3, left: 3 },
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: {
        0: { cellWidth: 8, halign: 'center' },
        1: { cellWidth: 42, fontStyle: 'bold' },
        2: { cellWidth: 28 },
        3: { cellWidth: 38 },
        4: { cellWidth: 28 },
        5: { cellWidth: 48 },
        6: { cellWidth: 30 },
        7: { cellWidth: 14, halign: 'center', fontStyle: 'bold' },
      },
      margin: { left: 10, right: 10 },
      didParseCell: (data: any) => {
        // Color the status column
        if (data.section === 'body' && data.column.index === 7) {
          if (data.cell.text[0] === '✓') {
            data.cell.styles.textColor = [16, 185, 129]
          } else {
            data.cell.styles.textColor = [203, 213, 225]
          }
        }
      },
    })

    // === FOOTER on every page ===
    const pageCount = doc.getNumberOfPages()
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i)
      // Footer line
      doc.setDrawColor(226, 232, 240)
      doc.setLineWidth(0.3)
      doc.line(14, pageH - 14, pageW - 14, pageH - 14)

      doc.setFontSize(7)
      doc.setTextColor(148, 163, 184)
      doc.text(`Pagina ${i} di ${pageCount}`, 14, pageH - 8)
      doc.setFont('helvetica', 'bold')
      doc.text('GESTIONE INFERMIERI © QuiCare Srl', pageW / 2, pageH - 8, { align: 'center' })
      doc.setFont('helvetica', 'normal')
      doc.text('Documento riservato', pageW - 14, pageH - 8, { align: 'right' })
    }

    doc.save(`QuiCare_Archivio_${title.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`)
    setSuccess('✅ PDF esportato con successo!')
  }

  // Total to display in search bar
  const totalToDisplay = searchQuery ? filteredNurses.length
    : selectedProvince ? filteredNurses.length
    : selectedRegion ? filteredNurses.length
    : nurses.length

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        {/* Skeleton KPI row */}
        <div className="grid grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-24 skeleton" />)}
        </div>
        {/* Skeleton toolbar */}
        <div className="h-14 skeleton" />
        {/* Skeleton cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3,4,5,6].map(i => (
            <div key={i} className="p-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full skeleton" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-3/4 skeleton" />
                  <div className="h-3 w-1/2 skeleton" />
                </div>
              </div>
              <div className="h-3 w-full skeleton" />
              <div className="h-3 w-2/3 skeleton" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  const qualColors: Record<string, string> = {
    'Infermiere': 'from-blue-500 to-indigo-500',
    'OSS': 'from-emerald-500 to-teal-500',
    'Fisioterapista': 'from-amber-500 to-orange-500',
    'Logopedista': 'from-violet-500 to-purple-500',
  }

  return (
    <div className="space-y-6">
      {/* Alerts */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-700 text-sm animate-fade-in">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
          <button onClick={() => setError('')} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-start gap-2 text-green-700 text-sm animate-fade-in max-h-60 overflow-y-auto">
          <Check className="w-4 h-4 mt-0.5 shrink-0" />
          <span className="whitespace-pre-line flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="ml-auto shrink-0"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Search bar + Export buttons */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Cerca per nome, cognome o CF..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>
        <button onClick={() => setShowArchiviati(!showArchiviati)}
          className={`px-3 py-2 text-xs font-bold rounded-xl border transition-all shrink-0 ${showArchiviati ? 'bg-slate-800 text-white border-slate-900' : 'bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200'}`}>
          {showArchiviati ? '● Nascondi Archiviati' : '○ Mostra Archiviati'}
        </button>
        <div className="text-sm text-slate-500 font-medium shrink-0">
          Totale: <span className="text-slate-900 font-bold">{totalToDisplay}</span> professionisti
        </div>
        <div className="flex items-center gap-2 ml-auto">
          {isStaffPlus && (
            <button
              onClick={() => handleOpenForm()}
              className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold text-white bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 rounded-xl hover:shadow-md hover:scale-[1.02] active:scale-[0.98] transition-all shrink-0"
              title="Aggiungi nuovo infermiere in archivio"
            >
              <Plus className="w-4 h-4 animate-pulse" />
              <span>Aggiungi Infermiere</span>
            </button>
          )}
          <button
            onClick={handleExportExcel}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl hover:bg-emerald-100 hover:shadow-md transition-all"
            title="Esporta in Excel"
          >
            <FileSpreadsheet className="w-4 h-4" />
            <span className="hidden sm:inline">Excel</span>
          </button>
          <button onClick={handleExportPDF} className="flex items-center gap-1.5 px-3 py-2 text-sm font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded-xl hover:bg-rose-100 transition-all">
            <Download className="w-4 h-4" /> PDF
          </button>
          <button onClick={() => { setShowAiMatcher(!showAiMatcher); setShowGeoSearch(false) }} className={`flex items-center gap-1.5 px-3 py-2 text-sm font-bold rounded-xl border transition-all ${showAiMatcher ? 'text-white bg-indigo-600 border-indigo-700' : 'text-indigo-700 bg-indigo-50 border-indigo-200 hover:bg-indigo-100'}`}>
            <Sparkles className="w-4 h-4" /> A.I. Smart Matcher
          </button>
          <button onClick={() => { setShowGeoSearch(!showGeoSearch); setShowAiMatcher(false) }} className={`flex items-center gap-1.5 px-3 py-2 text-sm font-bold rounded-xl border transition-all ${showGeoSearch ? 'text-white bg-indigo-600 border-indigo-700' : 'text-indigo-700 bg-indigo-50 border-indigo-200 hover:bg-indigo-100'}`}>
            <Crosshair className="w-4 h-4" /> Trova Vicino
          </button>
        </div>
      </div>

      {showAiMatcher && (
        <AiSmartMatcher 
          nurses={nurses} 
          onSelectNurse={(n) => {
            setSelectedNurse(n)
            setShowAiMatcher(false)
          }} 
        />
      )}

      {/* Geo-search panel */}
      {showGeoSearch && (
        <div className="bg-white rounded-2xl border border-indigo-200 shadow-lg p-5 animate-fade-in mt-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl">
              <Navigation className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-black text-slate-800">Trova Operatore Più Vicino</h3>
              <p className="text-xs text-slate-400">Inserisci l'indirizzo del paziente per trovare gli operatori più vicini</p>
            </div>
            <button onClick={() => { setShowGeoSearch(false); setGeoResults([]) }} className="ml-auto p-1.5 hover:bg-slate-100 rounded-lg">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={geoSearchAddress}
                onChange={e => setGeoSearchAddress(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleGeoSearch()}
                placeholder="Es: Via Roma 15, Milano, Lombardia"
                className="w-full pl-10 pr-3 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <button
              onClick={handleGeoSearch}
              disabled={geoSearching || !geoSearchAddress.trim()}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-bold rounded-xl shadow-md hover:shadow-lg disabled:opacity-50 text-sm"
            >
              {geoSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crosshair className="w-4 h-4" />}
              {geoSearching ? 'Ricerca...' : 'Cerca'}
            </button>
          </div>

          {geoError && <div className="text-sm text-red-600 bg-red-50 rounded-xl p-3 mb-3">{geoError}</div>}

          {geoSearching && (
            <div className="text-center py-6 text-slate-400">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
              <p className="text-sm">Geocoding operatori... (1 al secondo per rispettare i limiti API)</p>
              <p className="text-xs mt-1">Potrebbe richiedere qualche minuto con molti operatori</p>
            </div>
          )}

          {geoResults.length > 0 && (
            <div>
              <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                {geoResults.length} operatori trovati — ordinati per vicinanza
              </div>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {geoResults.map((r, idx) => (
                  <div
                    key={r.nurse.id}
                    onClick={() => setSelectedNurse(r.nurse)}
                    className={`p-3 rounded-xl border cursor-pointer transition-all hover:shadow-md ${
                      r.inRange ? 'bg-white border-green-200 hover:border-green-400' : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-black ${
                          idx === 0 ? 'bg-gradient-to-br from-amber-400 to-orange-500 text-white' :
                          idx === 1 ? 'bg-gradient-to-br from-slate-300 to-slate-400 text-white' :
                          idx === 2 ? 'bg-gradient-to-br from-amber-600 to-amber-700 text-white' :
                          'bg-slate-100 text-slate-500'
                        }`}>{idx + 1}</div>
                        <div>
                          <div className="font-bold text-slate-800 text-sm">{r.nurse.cognome} {r.nurse.nome}</div>
                          <div className="text-xs text-slate-400">{r.nurse.qualifica || ''} — {r.nurse.provincia}, {r.nurse.regione}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {r.zoneMatch && (
                          <span className="text-xs font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-lg">Zona ✓</span>
                        )}
                        <span className={`text-xs font-bold px-2.5 py-1 rounded-lg ${
                          r.inRange ? 'text-green-700 bg-green-50 border border-green-200' : 'text-slate-500 bg-slate-100'
                        }`}>
                          {r.distanceKm} km
                        </span>
                        {!r.inRange && r.nurse.km_spostamento && (
                          <span className="text-xs text-red-400">max {r.nurse.km_spostamento}km</span>
                        )}
                      </div>
                    </div>
                    {(r.nurse.telefono || r.nurse.zone_coperte) && (
                      <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                        {r.nurse.telefono && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {r.nurse.telefono}</span>}
                        {r.nurse.zone_coperte && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {r.nurse.zone_coperte}</span>}
                        {r.nurse.fasce_orarie && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {r.nurse.fasce_orarie}</span>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {/* Breadcrumbs */}
      {!searchQuery && (
        <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-slate-600 bg-white p-3 rounded-xl border border-slate-200 shadow-sm">
          {(selectedRegion || selectedProvince) && (
            <div className="flex items-center gap-1 mr-2 border-r border-slate-200 pr-3">
              <button onClick={goBack} className="flex items-center gap-1 px-2 py-1 text-xs font-bold text-slate-500 bg-slate-100 hover:bg-indigo-50 hover:text-indigo-600 rounded-md transition-colors">
                <ArrowLeft className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Indietro</span>
              </button>
              <button onClick={goHome} className="flex items-center gap-1 px-2 py-1 text-xs font-bold text-slate-500 bg-slate-100 hover:bg-indigo-50 hover:text-indigo-600 rounded-md transition-colors">
                <Globe className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Home</span>
              </button>
            </div>
          )}

          <MapPin className="w-4 h-4 text-slate-400" />
          <button onClick={goHome} className={`hover:text-indigo-600 transition-colors ${viewMode === 'regions' ? 'text-indigo-700 font-bold' : ''}`}>
            Italia
          </button>

          {selectedRegion && (
            <>
              <ChevronRight className="w-4 h-4 text-slate-300" />
              <button
                onClick={() => { setViewMode('provinces'); setSelectedProvince(null) }}
                className={`hover:text-indigo-600 transition-colors ${viewMode === 'provinces' ? 'text-indigo-700 font-bold' : ''}`}
              >
                {selectedRegion}
              </button>
            </>
          )}

          {selectedProvince && (
            <>
              <ChevronRight className="w-4 h-4 text-slate-300" />
              <span className="text-indigo-700 font-bold flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {selectedProvince}
              </span>
            </>
          )}

          {/* Add button */}
          {isStaffPlus && (selectedProvince || selectedRegion) && (
            <button
              onClick={() => handleOpenForm()}
              className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-md transition-all"
            >
              <Plus className="w-3.5 h-3.5" /> Aggiungi
            </button>
          )}
        </div>
      )}

      {/* KPI Dashboard — only on home view */}
      {!searchQuery && viewMode === 'regions' && (
        <div className="animate-fade-in">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 stagger-children">
            {/* Total */}
            <div onClick={() => { setKpiFilter('all'); setViewMode('nurse_list') }} className="relative overflow-hidden rounded-2xl p-5 hover:shadow-xl hover:scale-[1.02] transition-all group cursor-pointer">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 via-indigo-600 to-blue-700 opacity-90 group-hover:opacity-100 transition-opacity" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(255,255,255,0.15),transparent)]" />
              <div className="relative">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-white"><Users className="w-5 h-5" /></div>
                  <span className="text-[10px] font-bold text-white/70 bg-white/10 px-2 py-0.5 rounded-full uppercase tracking-wider">Totale</span>
                </div>
                <div className="text-3xl font-black text-white">{kpi.totale}</div>
                <div className="text-xs text-white/70 font-medium mt-1">Professionisti</div>
                <div className="text-[10px] text-white/50 mt-0.5">Clicca per vedere la lista →</div>
                <div className="mt-3 h-1 bg-white/20 rounded-full overflow-hidden"><div className="h-full bg-white/50 rounded-full" style={{ width: '100%' }} /></div>
              </div>
            </div>

            {/* Contratti */}
            <div onClick={() => { setKpiFilter('contratti'); setViewMode('nurse_list') }} className="relative overflow-hidden rounded-2xl p-5 hover:shadow-xl hover:scale-[1.02] transition-all group cursor-pointer">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 opacity-90 group-hover:opacity-100 transition-opacity" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(255,255,255,0.15),transparent)]" />
              <div className="relative">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-white"><FileText className="w-5 h-5" /></div>
                  <span className="text-[10px] font-bold text-white/70 bg-white/10 px-2 py-0.5 rounded-full uppercase tracking-wider">{kpi.totale > 0 ? Math.round(kpi.contrattiAttivi / kpi.totale * 100) : 0}%</span>
                </div>
                <div className="text-3xl font-black text-white">{kpi.contrattiAttivi}</div>
                <div className="text-xs text-white/70 font-medium mt-1">Contratti attivi</div>
                <div className="text-[10px] text-white/50 mt-0.5">Clicca per filtrare →</div>
                <div className="mt-3 h-1 bg-white/20 rounded-full overflow-hidden"><div className="h-full bg-white/50 rounded-full transition-all duration-700" style={{ width: `${kpi.totale > 0 ? (kpi.contrattiAttivi / kpi.totale * 100) : 0}%` }} /></div>
              </div>
            </div>

            {/* Assicurazioni */}
            <div onClick={() => { setKpiFilter('assicurazioni'); setViewMode('nurse_list') }} className="relative overflow-hidden rounded-2xl p-5 hover:shadow-xl hover:scale-[1.02] transition-all group cursor-pointer">
              <div className={`absolute inset-0 opacity-90 group-hover:opacity-100 transition-opacity ${kpi.assicurazioniScadute > 0 ? 'bg-gradient-to-br from-rose-500 via-red-600 to-pink-700' : 'bg-gradient-to-br from-amber-500 via-amber-600 to-orange-700'}`} />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(255,255,255,0.15),transparent)]" />
              <div className="relative">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-white"><Shield className="w-5 h-5" /></div>
                  {kpi.assicurazioniScadute > 0 && <span className="text-[10px] font-bold text-white bg-white/20 px-2 py-0.5 rounded-full animate-pulse">⚠️ {kpi.assicurazioniScadute} scadute</span>}
                </div>
                <div className="text-3xl font-black text-white">{kpi.assicurazioniInScadenza}</div>
                <div className="text-xs text-white/70 font-medium mt-1">In scadenza (30gg)</div>
                <div className="text-[10px] text-white/50 mt-0.5">Clicca per filtrare →</div>
                <div className="mt-3 h-1 bg-white/20 rounded-full overflow-hidden"><div className={`h-full rounded-full ${kpi.assicurazioniScadute > 0 ? 'bg-white/70' : 'bg-white/50'}`} style={{ width: `${Math.min(100, (kpi.assicurazioniInScadenza + kpi.assicurazioniScadute) / Math.max(1, kpi.totale) * 100)}%` }} /></div>
              </div>
            </div>

            {/* Regioni coperte */}
            <div onClick={() => document.getElementById('kpi-charts-section')?.scrollIntoView({ behavior: 'smooth' })} className="relative overflow-hidden rounded-2xl p-5 hover:shadow-xl hover:scale-[1.02] transition-all group cursor-pointer">
              <div className="absolute inset-0 bg-gradient-to-br from-violet-500 via-purple-600 to-fuchsia-700 opacity-90 group-hover:opacity-100 transition-opacity" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(255,255,255,0.15),transparent)]" />
              <div className="relative">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-white"><Globe className="w-5 h-5" /></div>
                  <span className="text-[10px] font-bold text-white/70 bg-white/10 px-2 py-0.5 rounded-full uppercase tracking-wider">Italia</span>
                </div>
                <div className="text-3xl font-black text-white">{kpi.topRegions.length}</div>
                <div className="text-xs text-white/70 font-medium mt-1">Regioni coperte</div>
                <div className="text-[10px] text-white/50 mt-0.5">Clicca per dettagli →</div>
                <div className="mt-3 h-1 bg-white/20 rounded-full overflow-hidden"><div className="h-full bg-white/50 rounded-full transition-all duration-700" style={{ width: `${kpi.topRegions.length / 20 * 100}%` }} /></div>
              </div>
            </div>
          </div>

          {/* Charts row */}
          <div id="kpi-charts-section" className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Top 5 Regioni */}
            <div className="bg-white/80 backdrop-blur-xl border border-slate-200/60 rounded-2xl p-5">
              <h3 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-indigo-500" /> Top Regioni
              </h3>
              <div className="space-y-3">
                {kpi.topRegions.map(([region, count]) => (
                  <div key={region} className="flex items-center gap-3">
                    <span className="text-xs text-slate-600 w-24 truncate font-medium">{region}</span>
                    <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-indigo-500 to-blue-500 rounded-full transition-all duration-700"
                        style={{ width: `${Math.max(8, (count / kpi.maxRegionCount) * 100)}%` }}
                      />
                    </div>
                    <span className="text-xs font-bold text-slate-700 w-8 text-right">{count}</span>
                  </div>
                ))}
                {kpi.topRegions.length === 0 && <p className="text-xs text-slate-400 italic">Nessun dato regionale</p>}
              </div>
            </div>

            {/* Qualifiche */}
            <div className="bg-white/80 backdrop-blur-xl border border-slate-200/60 rounded-2xl p-5">
              <h3 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2">
                <Award className="w-4 h-4 text-emerald-500" /> Distribuzione Qualifiche
              </h3>
              <div className="space-y-3">
                {Object.entries(kpi.qualificheMap).sort((a, b) => b[1] - a[1]).map(([qual, count]) => {
                  const color = qualColors[qual] || 'from-slate-400 to-slate-500'
                  return (
                    <div key={qual} className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${color}`} />
                      <span className="text-xs text-slate-600 flex-1 font-medium">{qual}</span>
                      <span className="text-xs font-bold text-slate-700">{count}</span>
                      <span className="text-xs text-slate-400">({nurses.length > 0 ? Math.round(count / nurses.length * 100) : 0}%)</span>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Italy Map — only on home view without search */}
      {!searchQuery && viewMode === 'regions' && (
        <div className="bg-white/80 backdrop-blur-xl border border-slate-200/60 rounded-2xl p-4 lg:p-6">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Map */}
            <div className="flex-shrink-0 relative" style={{ width: '100%', maxWidth: '380px' }}>
              <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                <Globe className="w-4 h-4 text-indigo-500" /> Distribuzione Nazionale
              </h3>
              <div
                className="relative"
                onMouseMove={e => {
                  const rect = e.currentTarget.getBoundingClientRect()
                  setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top })
                }}
              >
                <svg viewBox="0 0 540 460" style={{ width: '100%', height: 'auto' }}>
                  <defs>
                    <filter id="regShadow" x="-5%" y="-5%" width="110%" height="110%">
                      <feDropShadow dx="0" dy="1" stdDeviation="2" floodOpacity="0.12" />
                    </filter>
                    <linearGradient id="seaGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#e0f2fe" />
                      <stop offset="100%" stopColor="#bae6fd" />
                    </linearGradient>
                  </defs>
                  <rect x="0" y="0" width="540" height="460" rx="12" fill="url(#seaGrad)" opacity="0.3" />
                  {Object.entries(regionPaths).map(([region, data]) => {
                    const isHovered = hoveredRegion === region
                    const count = kpi.regionCount[region] || 0
                    return (
                      <g key={region}>
                        <path
                          d={data.d}
                          fill={getRegColor(region)}
                          stroke={isHovered ? '#4338ca' : '#64748b'}
                          strokeWidth={isHovered ? 2.5 : 0.8}
                          strokeLinejoin="round"
                          style={{
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            filter: isHovered ? 'url(#regShadow) brightness(1.1)' : 'none'
                          }}
                          onMouseEnter={() => setHoveredRegion(region)}
                          onMouseLeave={() => setHoveredRegion(null)}
                          onClick={() => { setSelectedRegion(region); setViewMode('provinces') }}
                        />
                        {count > 0 && !isHovered && (
                          <text
                            x={data.cx} y={data.cy}
                            textAnchor="middle" dominantBaseline="central"
                            style={{ fontSize: '9px', fontWeight: 800, fill: '#1e293b', pointerEvents: 'none', textShadow: '0 0 3px white, 0 0 3px white' }}
                          >
                            {count}
                          </text>
                        )}
                      </g>
                    )
                  })}
                </svg>

                {/* Tooltip */}
                {hoveredRegion && (
                  <div style={{
                    position: 'absolute',
                    left: `${Math.min(mousePos.x + 12, 260)}px`,
                    top: `${mousePos.y - 50}px`,
                    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                    color: 'white',
                    padding: '0.5rem 0.8rem',
                    borderRadius: '0.5rem',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    pointerEvents: 'none',
                    zIndex: 50,
                    boxShadow: '0 8px 25px rgba(0,0,0,0.25)',
                    whiteSpace: 'nowrap',
                    border: '1px solid rgba(255,255,255,0.1)'
                  }}>
                    <div style={{ fontSize: '0.8rem' }}>{hoveredRegion}</div>
                    <div style={{ fontSize: '0.65rem', color: '#93c5fd', fontWeight: 500, marginTop: '2px' }}>
                      {kpi.regionCount[hoveredRegion] || 0} infermieri
                    </div>
                  </div>
                )}
              </div>
              {/* Legend */}
              <div className="flex items-center gap-3 mt-3 text-xs text-slate-500">
                <span className="flex items-center gap-1.5">
                  <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '3px', background: '#e2e8f0', border: '1px solid #cbd5e1' }} /> 0
                </span>
                <span style={{ display: 'inline-block', width: '80px', height: '8px', borderRadius: '4px', background: 'linear-gradient(to right, #e2e8f0, #4338ca)' }} />
                <span className="font-bold text-indigo-700">{kpi.maxRegionCount}</span>
              </div>
              <p className="text-xs text-slate-400 mt-2 italic">Clicca una regione per le province</p>
            </div>

            {/* Region cards grid */}
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-bold text-slate-700 mb-3">Tutte le Regioni</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {Object.keys(regionsData).sort().map(region => {
                  const count = kpi.regionCount[region] || 0
                  return (
                    <button
                      key={region}
                      onClick={() => { setSelectedRegion(region); setViewMode('provinces') }}
                      className={`p-3 rounded-xl text-left border transition-all hover:shadow-md hover:-translate-y-0.5 ${count > 0 ? 'bg-white border-slate-200 hover:border-indigo-300' : 'bg-slate-50 border-slate-100 opacity-60'}`}
                    >
                      <div className="text-xs font-bold text-slate-700 truncate">{region}</div>
                      <div className={`text-lg font-black mt-1 ${count > 0 ? 'text-indigo-600' : 'text-slate-300'}`}>{count}</div>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Province view */}
      {!searchQuery && viewMode === 'provinces' && selectedRegion && (
        <div className="animate-fade-in">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {(regionsData[selectedRegion] || []).map(province => {
              const count = nurses.filter(n => n.provincia === province).length
              return (
                <button
                  key={province}
                  onClick={() => { setSelectedProvince(province); setViewMode('nurse_list') }}
                  className={`p-4 rounded-xl text-left border transition-all hover:shadow-md hover:-translate-y-0.5 ${count > 0 ? 'bg-white border-slate-200 hover:border-indigo-300' : 'bg-slate-50 border-slate-100'}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-4 h-4 text-indigo-400" />
                    <span className="text-sm font-bold text-slate-700 truncate">{province}</span>
                  </div>
                  <div className={`text-2xl font-black ${count > 0 ? 'text-indigo-600' : 'text-slate-300'}`}>{count}</div>
                  <div className="text-xs text-slate-400 mt-1">professionisti</div>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Nurse list (search results or province drill-down) */}
      {(searchQuery || viewMode === 'nurse_list') && (
        <div className="animate-fade-in">
          {/* KPI Filter active banner */}
          {kpiFilter && (
            <div className="mb-4 p-3 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-2xl flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-bold text-indigo-700">
                {kpiFilter === 'all' && <><Users className="w-4 h-4" /> Tutti i professionisti</>}
                {kpiFilter === 'contratti' && <><FileText className="w-4 h-4" /> Filtrando: Contratti attivi</>}
                {kpiFilter === 'assicurazioni' && <><Shield className="w-4 h-4" /> Filtrando: Assicurazioni in scadenza/scadute</>}
                <span className="text-xs text-indigo-400 font-normal ml-2">({filteredNurses.length} risultati)</span>
              </div>
              <button onClick={() => { setKpiFilter(null); setViewMode('regions') }} className="text-xs font-bold text-indigo-500 hover:text-indigo-700 px-3 py-1 bg-white rounded-xl border border-indigo-200 hover:shadow transition-all">✕ Rimuovi filtro</button>
            </div>
          )}
          {filteredNurses.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-sm">
              <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              {searchQuery ? 'Nessun risultato trovato' : 'Nessun professionista in questa zona'}
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-4 py-3 font-bold text-slate-600 text-xs uppercase">Nome</th>
                      <th className="text-left px-4 py-3 font-bold text-slate-600 text-xs uppercase">Qualifica</th>
                      <th className="text-left px-4 py-3 font-bold text-slate-600 text-xs uppercase hidden md:table-cell">Contatti</th>
                      <th className="text-left px-4 py-3 font-bold text-slate-600 text-xs uppercase hidden lg:table-cell">Contratto</th>
                      <th className="text-left px-4 py-3 font-bold text-slate-600 text-xs uppercase hidden lg:table-cell">Assicurazione</th>
                      {isStaffPlus && <th className="text-right px-4 py-3 font-bold text-slate-600 text-xs uppercase">Azioni</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredNurses.map(nurse => {
                      const assurance = getAssuranceStatus(nurse)
                      const assColor = assurance.color === 'green' ? 'text-green-600 bg-green-50'
                        : assurance.color === 'amber' ? 'text-amber-600 bg-amber-50'
                        : 'text-red-600 bg-red-50'
                      return (
                        <tr key={nurse.id} onClick={() => setSelectedNurse(nurse)} className="border-b border-slate-100 hover:bg-indigo-50/50 cursor-pointer transition-colors">
                          <td className="px-4 py-3">
                            <div className="font-bold text-slate-800">{nurse.cognome} {nurse.nome}</div>
                            <div className="text-xs text-slate-400">{nurse.regione} — {nurse.provincia}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs font-medium text-slate-600">{nurse.qualifica || '—'}</span>
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell">
                            <div className="space-y-1">
                              {nurse.telefono && <div className="flex items-center gap-1 text-xs text-slate-500"><Phone className="w-3 h-3" /> {nurse.telefono}</div>}
                              {nurse.email && <div className="flex items-center gap-1 text-xs text-slate-500"><Mail className="w-3 h-3" /> {nurse.email}</div>}
                            </div>
                          </td>
                          <td className="px-4 py-3 hidden lg:table-cell">
                            <span className={`text-xs font-bold px-2 py-1 rounded-lg ${nurse.contratto_firmato ? 'text-green-600 bg-green-50' : 'text-slate-400 bg-slate-50'}`}>
                              {nurse.contratto_firmato ? 'Firmato' : 'Non firmato'}
                            </span>
                          </td>
                          <td className="px-4 py-3 hidden lg:table-cell">
                            <span className={`text-xs font-bold px-2 py-1 rounded-lg ${assColor}`}>
                              {assurance.label}
                            </span>
                          </td>
                          {isStaffPlus && (
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <button onClick={(e) => { e.stopPropagation(); handleOpenForm(nurse) }} className="p-1.5 hover:bg-indigo-50 rounded-lg text-indigo-500 transition-colors" title="Modifica">
                                  <Edit3 className="w-4 h-4" />
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); handleDeleteNurse(nurse.id, `${nurse.cognome} ${nurse.nome}`) }} className="p-1.5 hover:bg-red-50 rounded-lg text-red-400 transition-colors" title="Elimina">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Nurse form modal (Advanced AI-Powered Glassmorphism) */}
      {showNurseForm && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-50 flex items-center justify-center p-4 sm:p-6" onClick={() => setShowNurseForm(false)}>
          <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col border border-white/20 animate-modal-in overflow-hidden relative" onClick={e => e.stopPropagation()}>
            <div className="absolute top-0 inset-x-0 h-40 bg-gradient-to-br from-indigo-600 via-purple-600 to-emerald-600 opacity-10" />
            
            <div className="px-6 py-5 border-b border-slate-200/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4 z-10 relative bg-white/50 backdrop-blur-md">
              <div>
                <h3 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-indigo-700 to-purple-700">
                  {editingNurse ? 'Modifica Infermiere' : 'Nuovo Infermiere'}
                </h3>
                <p className="text-sm text-slate-500 font-medium">Compila i dati o usa l'intelligenza artificiale per l'auto-compilazione.</p>
              </div>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 rounded-xl text-white text-sm font-bold cursor-pointer transition-all shadow-md hover:shadow-lg shadow-emerald-500/20">
                  {ocrProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                  {ocrProcessing ? (ocrProgress || 'Elaborazione...') : 'A.I. Auto-Compila da CV'}
                  <input type="file" className="hidden" accept="image/*,.pdf,.doc,.docx,.txt" multiple onChange={e => { const fs = e.target.files; if (fs && fs.length > 0) handleOCRNurseForm(fs); e.target.value = '' }} disabled={ocrProcessing} />
                </label>
                <button onClick={() => setShowNurseForm(false)} className="p-2 hover:bg-slate-200/50 rounded-xl transition-colors"><X className="w-6 h-6 text-slate-500" /></button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 z-10 space-y-8 bg-gradient-to-b from-transparent to-slate-50/50">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Colonna SX */}
                <div className="space-y-6">
                  <FormSection title="Anagrafica & Contatti" icon={<User className="w-4 h-4 text-indigo-500" />} color="indigo">
                    <div className="grid grid-cols-2 gap-4">
                      <FormField label="Nome *" value={formData.nome || ''} onChange={v => setFormData({ ...formData, nome: v })} />
                      <FormField label="Cognome *" value={formData.cognome || ''} onChange={v => setFormData({ ...formData, cognome: v })} />
                      <div className="col-span-2">
                        <FormField label="Codice Fiscale" value={formData.codice_fiscale || ''} onChange={v => setFormData({ ...formData, codice_fiscale: v?.toUpperCase() })} />
                      </div>
                      <div className="col-span-2">
                        <FormField label="Partita IVA *" value={formData.partita_iva || ''} onChange={v => setFormData({ ...formData, partita_iva: v })} />
                      </div>
                      <FormField label="Telefono" value={formData.telefono || ''} onChange={v => setFormData({ ...formData, telefono: v })} />
                      <FormField label="Email" value={formData.email || ''} onChange={v => setFormData({ ...formData, email: v })} type="email" />
                      <FormField label="PEC" value={formData.pec || ''} onChange={v => setFormData({ ...formData, pec: v })} type="email" />
                    </div>
                  </FormSection>

                  <FormSection title="Coordinate Geografiche" icon={<MapPin className="w-4 h-4 text-emerald-500" />} color="emerald">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[10px] font-black text-slate-500 mb-1.5 uppercase tracking-wider">Regione *</label>
                        <select value={formData.regione || ''} onChange={e => setFormData({ ...formData, regione: e.target.value, provincia: '' })} className="w-full px-3.5 py-3 border border-slate-200/80 rounded-xl text-sm bg-white shadow-sm focus:border-indigo-400 focus:ring-4 focus:ring-indigo-400/20 outline-none transition-all text-slate-700">
                          <option value="">Seleziona</option>
                          {Object.keys(regionsData).sort().map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] font-black text-slate-500 mb-1.5 uppercase tracking-wider">Provincia *</label>
                        <select value={formData.provincia || ''} onChange={e => setFormData({ ...formData, provincia: e.target.value })} disabled={!formData.regione} className="w-full px-3.5 py-3 border border-slate-200/80 rounded-xl text-sm bg-white shadow-sm focus:border-indigo-400 focus:ring-4 focus:ring-indigo-400/20 outline-none transition-all text-slate-700">
                          <option value="">Seleziona</option>
                          {formData.regione && (regionsData[formData.regione] || []).map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </div>
                      <div className="col-span-2">
                        <div className="flex items-end gap-2">
                          <div className="flex-1">
                            <FormField label="Indirizzo" value={formData.indirizzo || ''} onChange={v => setFormData({ ...formData, indirizzo: v })} />
                          </div>
                          <button
                            title="Normalizza Indirizzo via A.I."
                            onClick={(e) => { e.preventDefault(); handleNormalizeAddress() }}
                            disabled={!formData.indirizzo || normalizingAddress}
                            className="bg-indigo-50 border border-indigo-200 text-indigo-700 p-3 h-[46px] rounded-xl flex items-center justify-center hover:bg-indigo-100 disabled:opacity-50 transition-colors shadow-sm"
                          >
                            {normalizingAddress ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>
                      <div className="col-span-2">
                        <FormField label="Indirizzo Residenza (se diverso)" value={formData.residenza_contratto || ''} onChange={v => setFormData({ ...formData, residenza_contratto: v })} />
                      </div>
                    </div>
                  </FormSection>

                  <FormSection title="Dati Operativi" icon={<Navigation className="w-4 h-4 text-amber-500" />} color="amber">
                    <div className="grid grid-cols-2 gap-4">
                      <FormField label="Raggio Km" value={formData.km_spostamento || ''} onChange={v => setFormData({ ...formData, km_spostamento: v })} />
                      <FormField label="Zone Coperte" value={formData.zone_coperte || ''} onChange={v => setFormData({ ...formData, zone_coperte: v })} />
                      <FormField label="Disponibilità" value={formData.disponibilita || ''} onChange={v => setFormData({ ...formData, disponibilita: v })} />
                      <FormField label="Fasce Orarie" value={formData.fasce_orarie || ''} onChange={v => setFormData({ ...formData, fasce_orarie: v })} />
                    </div>
                  </FormSection>
                </div>

                {/* Colonna DX */}
                <div className="space-y-6">
                  <FormSection title="Profilo Professionale" icon={<Briefcase className="w-4 h-4 text-violet-500" />} color="violet">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[10px] font-black text-slate-500 mb-1.5 uppercase tracking-wider">Qualifica</label>
                        <select value={formData.qualifica || 'Infermiere'} onChange={e => setFormData({ ...formData, qualifica: e.target.value })} className="w-full px-3.5 py-3 border border-slate-200/80 rounded-xl text-sm bg-slate-50 text-slate-700 shadow-sm outline-none cursor-pointer">
                          <option value="Infermiere">Infermiere</option>
                        </select>
                      </div>
                      <FormField label="Tipo Contratto" value={formData.contratto || ''} onChange={v => setFormData({ ...formData, contratto: v })} />
                      
                      <div className="col-span-2 pt-2 pb-1 border-b border-slate-100 flex items-center justify-between">
                        <label className="text-sm font-bold text-slate-700 flex items-center gap-2">
                          Contratto Firmato Status
                        </label>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input type="checkbox" className="sr-only peer" checked={formData.contratto_firmato || false} onChange={e => setFormData({ ...formData, contratto_firmato: e.target.checked })} />
                          <div className="w-11 h-6 bg-slate-200 rounded-full peer peer-focus:ring-4 peer-focus:ring-indigo-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                        </label>
                      </div>

                      <div className="col-span-2">
                        <FormField label="IBAN" value={formData.iban || ''} onChange={v => setFormData({ ...formData, iban: v })} />
                      </div>
                      <div className="col-span-2">
                        <FormField label="Residenza Fiscale" value={formData.residenza_contratto || ''} onChange={v => setFormData({ ...formData, residenza_contratto: v })} />
                      </div>
                    </div>
                  </FormSection>

                  <FormSection title="Albo & Assicurazione" icon={<Shield className="w-4 h-4 text-cyan-500" />} color="cyan">
                    <div className="grid grid-cols-2 gap-4">
                      <FormField label="OPI Provincia" value={formData.opi_provincia || ''} onChange={v => setFormData({ ...formData, opi_provincia: v })} />
                      <FormField label="OPI Numero" value={formData.opi_numero || ''} onChange={v => setFormData({ ...formData, opi_numero: v })} />
                      <FormField label="Data Abilitazione" value={formData.data_abilitazione || ''} onChange={v => setFormData({ ...formData, data_abilitazione: v })} type="date" />
                      <FormField label="Iscrizione Albo" value={formData.data_iscrizione_albo || ''} onChange={v => setFormData({ ...formData, data_iscrizione_albo: v })} type="date" />
                      
                      <div className="col-span-2 pt-3 border-t border-slate-100 mt-2">
                        <FormField label="Polizza Assicurazione" value={formData.assicurazione || ''} onChange={v => setFormData({ ...formData, assicurazione: v })} />
                      </div>
                      <FormField label="Scadenza Ass." value={formData.assicurazione_scadenza || ''} onChange={v => setFormData({ ...formData, assicurazione_scadenza: v })} type="date" />
                      <FormField label="Massimale" value={formData.polizza_massimale || ''} onChange={v => setFormData({ ...formData, polizza_massimale: v })} />
                    </div>
                  </FormSection>

                  <FormSection title="Progetti & Formazione" icon={<ShieldCheck className="w-4 h-4 text-indigo-500" />} color="slate">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-[10px] font-black text-slate-500 mb-2 uppercase tracking-wider">Progetti Attivi (check multiplo)</label>
                        <div className="flex flex-wrap gap-2">
                          {['QuiConTe', 'BluClick', 'RenCare'].map(progetto => {
                            const isChecked = (formData.progetti_attivi || []).includes(progetto);
                            return (
                              <label key={progetto} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${isChecked ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                                <input
                                  type="checkbox"
                                  className="hidden"
                                  checked={isChecked}
                                  onChange={(e) => {
                                    const current = formData.progetti_attivi || [];
                                    if (e.target.checked) setFormData({ ...formData, progetti_attivi: [...current, progetto] });
                                    else setFormData({ ...formData, progetti_attivi: current.filter(p => p !== progetto) });
                                  }}
                                />
                                <div className={`w-4 h-4 rounded border flex items-center justify-center ${isChecked ? 'bg-indigo-500 border-indigo-500' : 'border-slate-300'}`}>
                                  {isChecked && <Check className="w-3 h-3 text-white" />}
                                </div>
                                <span className="text-sm font-bold">{progetto}</span>
                              </label>
                            )
                          })}
                        </div>
                      </div>
                      
                      <div className="pt-4 border-t border-slate-100">
                        <label className="block text-[10px] font-black text-slate-500 mb-2 uppercase tracking-wider">Formazione Eseguita</label>
                        <div className="flex flex-wrap gap-3 mb-4">
                          {[
                            { key: 'formazione_fv', label: 'FV' },
                            { key: 'formazione_qualita', label: 'Qualità' },
                            { key: 'formazione_farmaco', label: 'Farmaco' },
                            { key: 'formazione_dispositivo', label: 'Dispositivo' },
                          ].map(f => {
                            const isChecked = formData[f.key as keyof typeof formData]
                            return (
                              <label key={f.key} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border cursor-pointer transition-colors ${isChecked ? 'bg-teal-50 border-teal-200 text-teal-700' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                                <input type="checkbox" className="hidden" checked={isChecked || false} onChange={e => setFormData({ ...formData, [f.key]: e.target.checked })} />
                                <div className={`w-3.5 h-3.5 rounded flex items-center justify-center ${isChecked ? 'bg-teal-500' : 'bg-slate-200'}`}>
                                  {isChecked && <Check className="w-2.5 h-2.5 text-white" />}
                                </div>
                                <span className="text-xs font-bold">{f.label}</span>
                              </label>
                            )
                          })}
                        </div>
                        <FormField label="Data Formazione / Esec." value={formData.data_formazione || ''} onChange={v => setFormData({ ...formData, data_formazione: v })} type="date" />
                      </div>
                    </div>
                  </FormSection>
                </div>
              </div>

              <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200">
                <label className="block text-[10px] font-black text-slate-500 mb-1.5 uppercase tracking-wider">Note (Opzionale)</label>
                <textarea
                  value={formData.note || ''}
                  onChange={e => setFormData({ ...formData, note: e.target.value })}
                  rows={2}
                  className="w-full px-3.5 py-3 border border-slate-200/80 rounded-xl text-sm bg-white shadow-sm focus:border-indigo-400 focus:ring-4 focus:ring-indigo-400/20 outline-none transition-all placeholder-slate-300 resize-none"
                  placeholder="Aggiungi annotazioni aggiuntive sul professionista..."
                ></textarea>
              </div>
            </div>

            <div className="p-5 bg-white/80 backdrop-blur-xl border-t border-slate-200/50 flex gap-3 z-10 relative">
              <button onClick={() => setShowNurseForm(false)} className="px-6 py-3 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 rounded-xl text-sm font-bold transition-colors">
                Annulla
              </button>
              <button onClick={handleSaveNurse} className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold py-3 px-6 rounded-xl shadow-lg shadow-indigo-500/30 transition-all flex items-center justify-center gap-2 text-sm">
                <Check className="w-4 h-4" />
                {editingNurse ? 'Salva Modifiche' : 'Crea Professionista'}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Premium Nurse Detail Panel */}
      {selectedNurse && (() => {
        const n = selectedNurse
        const noteHist = (n.note_history as any[]) || []
        const stepHist = (n.step_history as any[]) || []
        const assurance = getAssuranceStatus(n)
        const initials = `${(n.nome || '?')[0]}${(n.cognome || '?')[0]}`.toUpperCase()
        return (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-center justify-center p-4" onClick={() => setSelectedNurse(null)}>
            <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden border border-white/20 animate-modal-in" onClick={e => e.stopPropagation()}>
              {/* Clean Header — Patient-style */}
              <div className="p-6 pb-0 space-y-5">
                {/* Top Bar: Back, Name, Actions */}
                <div className="flex items-center gap-4">
                  <button onClick={() => setSelectedNurse(null)} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
                    <X className="w-5 h-5 text-slate-500" />
                  </button>
                  <div className="flex-1">
                    <h2 className="text-2xl font-black text-slate-800">{n.cognome} {n.nome}</h2>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-xs font-bold px-2.5 py-0.5 rounded-lg bg-indigo-100 text-indigo-700">{n.qualifica || 'Non specificata'}</span>
                      {n.tipo_contratto && <span className={`text-xs font-bold px-2.5 py-0.5 rounded-lg ${n.tipo_contratto === 'piva' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{n.tipo_contratto === 'piva' ? 'P.IVA' : 'Ritenuta'}</span>}
                      <span className={`text-xs font-bold px-2.5 py-0.5 rounded-lg ${n.contratto_firmato ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                        {n.contratto_firmato ? '✅ Contratto Attivo' : '⏳ No Contratto'}
                      </span>
                      <span className={`text-xs font-bold px-2.5 py-0.5 rounded-lg ${assurance.color === 'green' ? 'bg-emerald-100 text-emerald-700' : assurance.color === 'amber' ? 'bg-amber-100 text-amber-700' : assurance.color === 'red' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500'}`}>
                        🛡️ {assurance.label}
                      </span>
                      {n.km_spostamento && <span className="text-xs font-bold px-2.5 py-0.5 rounded-lg bg-slate-100 text-slate-600">📍 {n.km_spostamento} km</span>}
                    </div>
                  </div>
                  {isStaffPlus && (
                    <div className="flex items-center gap-2">
                      <button onClick={() => { setSelectedNurse(null); handleOpenForm(n) }} className="p-2 hover:bg-slate-100 rounded-xl text-slate-500 transition-colors" title="Modifica dati">
                        <Edit3 className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Billing KPI banner */}
                {nurseBilling && nurseBilling.totaleAccessi > 0 && (
                  <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl p-4 flex items-center gap-4 text-white">
                    <div className="flex-1">
                      <div className="text-xs font-bold text-white/70 uppercase tracking-wider">Fatturato Globale</div>
                      <div className="text-lg font-black mt-0.5">€{nurseBilling.totaleLordo.toFixed(2)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold">{nurseBilling.totaleAccessi} accessi</div>
                      <div className="text-xs text-white/70">{nurseBilling.totaleOre}h lavorate</div>
                    </div>
                  </div>
                )}

                {/* Quick Actions */}
                <div className="flex items-center gap-2 flex-wrap">
                  {n.telefono && <a href={`tel:${n.telefono}`} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-xl text-xs font-bold transition-all border border-blue-200"><Phone className="w-3.5 h-3.5" /> Chiama</a>}
                  {n.email && <button onClick={(e) => { e.preventDefault(); sendAptosFormattedEmail(n.email || '', '', ''); }} className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-50 hover:bg-violet-100 text-violet-700 rounded-xl text-xs font-bold transition-all border border-violet-200"><Mail className="w-3.5 h-3.5" /> Email</button>}
                  {n.indirizzo && <a href={`https://maps.google.com/?q=${encodeURIComponent(n.indirizzo + ', ' + n.provincia)}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-xl text-xs font-bold transition-all border border-emerald-200"><MapPin className="w-3.5 h-3.5" /> Mappa</a>}
                  
                </div>

                {/* Tab Navigation — Patient-style */}
                <div className="flex gap-1 bg-slate-100 rounded-xl p-1 overflow-x-auto">
                  {([
                    { key: 'anagrafica', label: '👤 Profilo' },
                    { key: 'professionale', label: '🛡️ Fiscale' },
                    { key: 'diario', label: '📝 Diario' },
                    { key: 'documenti', label: '📁 Documenti' },
                    { key: 'pazienti', label: '👥 In Carico' },
                  ] as const).map(tab => (
                    <button key={tab.key} onClick={() => { setNurseDetailTab(tab.key as any); if (tab.key === 'diario') fetchNurseDiary(n.id); if (tab.key === 'documenti') fetchNurseDocuments(n.id); }}
                      className={`flex-1 min-w-[100px] flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-bold transition-all ${
                        nurseDetailTab === tab.key ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}>
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

            <div className="p-6 space-y-5 flex-1 overflow-y-auto">
                
                {/* ═══ TAB: ANAGRAFICA E PROFILO ═══ */}
                {nurseDetailTab === 'anagrafica' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Contatti */}
                      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
                        <div className="text-xs font-bold text-slate-400 uppercase flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> Contatti</div>
                        {n.telefono && <div className="flex items-center gap-2 text-sm"><Phone className="w-4 h-4 text-blue-500" /> <a href={`tel:${n.telefono}`} className="text-blue-600 hover:underline">{n.telefono}</a></div>}
                        {n.email && <div className="flex items-center gap-2 text-sm"><Mail className="w-4 h-4 text-indigo-500" /> <button onClick={(e) => { e.preventDefault(); sendAptosFormattedEmail(n.email || '', '', ''); }} className="text-indigo-600 hover:underline truncate text-left">{n.email}</button></div>}
                        {n.pec && <div className="flex items-center gap-2 text-sm"><Mail className="w-4 h-4 text-violet-500" /> <span className="text-slate-600 truncate" title={n.pec}>PEC: {n.pec}</span></div>}
                        {n.codice_fiscale && <div className="flex items-center gap-2 text-sm mt-2 pt-2 border-t border-slate-100"><FileText className="w-4 h-4 text-amber-500" /> <span className="font-mono text-slate-700">{n.codice_fiscale}</span></div>}
                        {n.partita_iva && <div className="flex items-center gap-2 text-sm"><FileText className="w-4 h-4 text-orange-500" /> <span className="text-slate-700">P.IVA: <span className="font-mono font-bold">{n.partita_iva}</span></span></div>}
                      </div>

                      {/* Coordinate Geografiche */}
                      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
                        <div className="text-xs font-bold text-slate-400 uppercase flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Coordinate</div>
                        <div className="flex items-center gap-2 text-sm"><MapPin className="w-4 h-4 text-emerald-500" /> {n.regione} — {n.provincia}</div>
                        {n.indirizzo && <div className="flex items-center gap-2 text-sm text-slate-600"><MapPin className="w-4 h-4 text-slate-400" /> {n.indirizzo}</div>}
                        {n.residenza && <div className="flex items-center gap-2 text-sm text-slate-600"><MapPin className="w-4 h-4 text-teal-400" /> Residenza: {n.residenza}</div>}
                        {n.domicilio && <div className="flex items-center gap-2 text-sm text-slate-600"><MapPin className="w-4 h-4 text-cyan-400" /> Domicilio: {n.domicilio}</div>}
                        {n.residenza_contratto && <div className="flex items-center gap-2 text-sm text-slate-600"><MapPin className="w-4 h-4 text-orange-400" /> Residenza Fiscale: {n.residenza_contratto}</div>}
                        {n.zone_coperte && <div className="flex items-center gap-2 text-sm mt-2 pt-2 border-t border-slate-100"><MapPin className="w-4 h-4 text-teal-500" /> <span className="text-slate-700">Zone: {n.zone_coperte}</span></div>}
                        {n.km_spostamento && <div className="flex items-center gap-2 text-sm"><MapPin className="w-4 h-4 text-sky-500" /> Max {n.km_spostamento} km</div>}
                      </div>
                    </div>

                    {/* Dati Contrattuali e Professionali */}
                    <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
                      <div className="text-xs font-bold text-slate-400 uppercase flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> Contratto e Professione</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {n.qualifica && (
                          <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                            <span className="text-[10px] text-slate-400 block uppercase font-bold mb-0.5">Qualifica</span>
                            <div className="font-medium text-slate-700">{n.qualifica}</div>
                          </div>
                        )}
                        {n.tipo_contratto && (
                          <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                            <span className="text-[10px] text-slate-400 block uppercase font-bold mb-0.5">Tipo Contratto</span>
                            <div className="font-medium text-slate-700">{n.tipo_contratto === 'piva' ? 'Partita IVA' : n.tipo_contratto === 'ritenuta' ? "Ritenuta d'Acconto" : n.tipo_contratto}</div>
                          </div>
                        )}
                        {n.contratto && (
                          <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                            <span className="text-[10px] text-slate-400 block uppercase font-bold mb-0.5">Dettagli Contratto</span>
                            <div className="font-medium text-slate-700">{n.contratto}</div>
                          </div>
                        )}
                        <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                          <span className="text-[10px] text-slate-400 block uppercase font-bold mb-0.5">Contratto Firmato</span>
                          <div className={`font-bold ${n.contratto_firmato ? 'text-green-600' : 'text-slate-400'}`}>{n.contratto_firmato ? '✅ Sì' : '❌ No'}</div>
                          {n.contratto_data_firma && <div className="text-[10px] text-slate-500 mt-0.5">Firmato il {new Date(n.contratto_data_firma).toLocaleDateString('it-IT')}</div>}
                        </div>
                        {n.iban && (
                          <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                            <span className="text-[10px] text-slate-400 block uppercase font-bold mb-0.5">IBAN</span>
                            <div className="font-mono text-xs font-bold text-slate-700 break-all">{n.iban}</div>
                          </div>
                        )}
                        {n.data_colloquio && (
                          <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                            <span className="text-[10px] text-slate-400 block uppercase font-bold mb-0.5">Data Colloquio</span>
                            <div className="font-medium text-slate-700">{new Date(n.data_colloquio).toLocaleDateString('it-IT')}</div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Albo e Assicurazione */}
                    {(n.opi_provincia || n.assicurazione || n.polizza_massimale) && (
                      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
                        <div className="text-xs font-bold text-slate-400 uppercase flex items-center gap-1">🛡️ Albo e Assicurazione</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {n.opi_provincia && (
                            <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                              <span className="text-[10px] text-slate-400 block uppercase font-bold mb-0.5">Albo OPI</span>
                              <div className="font-medium text-slate-700">{n.opi_provincia} — <span className="text-indigo-600 font-bold">{n.opi_numero || 'N/D'}</span></div>
                              {(n.data_abilitazione || n.data_iscrizione_albo) && (
                                <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-1"><Calendar className="w-3 h-3"/> Iscr: {new Date(n.data_iscrizione_albo || n.data_abilitazione!).toLocaleDateString('it-IT')}</div>
                              )}
                              {n.data_abilitazione && <div className="text-[10px] text-slate-500 flex items-center gap-1"><Calendar className="w-3 h-3"/> Abilitazione: {new Date(n.data_abilitazione).toLocaleDateString('it-IT')}</div>}
                            </div>
                          )}
                          {(n.assicurazione || n.polizza_massimale) && (
                            <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                              <span className="text-[10px] text-slate-400 block uppercase font-bold mb-0.5">Assicurazione RCT</span>
                              <div className="font-medium text-slate-700">{n.assicurazione || 'Non specificata'}</div>
                              {(n.polizza_massimale || n.assicurazione_scadenza) && (
                                <div className="text-[10px] text-slate-500 mt-1 flex justify-between items-center">
                                  {n.polizza_massimale && <span>Max: €{n.polizza_massimale}</span>}
                                  {n.assicurazione_scadenza && <span className={`flex items-center gap-1 font-bold ${assurance.color === 'red' ? 'text-red-500' : assurance.color === 'amber' ? 'text-amber-500' : 'text-emerald-500'}`}><Calendar className="w-3 h-3"/> Scad: {new Date(n.assicurazione_scadenza).toLocaleDateString('it-IT')}</span>}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* CV e Tags */}
                    {(n.link_c_v || n.tags) && (
                      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
                        <div className="text-xs font-bold text-slate-400 uppercase flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> CV e Tags</div>
                        {n.link_c_v && (
                          <a href={n.link_c_v} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-xl text-sm font-bold transition-all border border-indigo-200">
                            <ExternalLink className="w-4 h-4" /> Visualizza CV
                          </a>
                        )}
                        {n.tags && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {n.tags.split(',').map((tag, i) => (
                              <span key={i} className="px-2.5 py-0.5 bg-purple-50 text-purple-700 rounded-lg text-xs font-bold border border-purple-200">{tag.trim()}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Setup Lavorativo */}
                    {(n.disponibilita || n.fasce_orarie) && (
                      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
                        <div className="text-xs font-bold text-slate-400 uppercase flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Disponibilità Lavorativa</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {n.disponibilita && <div className="flex items-center gap-2 text-sm"><Clock className="w-4 h-4 text-cyan-500" /> {n.disponibilita}</div>}
                          {n.fasce_orarie && <div className="flex items-center gap-2 text-sm"><Clock className="w-4 h-4 text-pink-500" /> {n.fasce_orarie}</div>}
                        </div>
                      </div>
                    )}

                    {/* Note */}
                    {(n.note || n.interview_notes) && (
                      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
                        <div className="text-xs font-bold text-slate-400 uppercase flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> Note e Storico</div>
                        {n.interview_notes && <div><span className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Colloquio</span><p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-xl border border-slate-100">{n.interview_notes}</p></div>}
                        {n.note && <div><span className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Note</span><p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-xl border border-slate-100 whitespace-pre-wrap">{n.note}</p></div>}
                      </div>
                    )}
                  </div>
                )}

                {/* ═══ TAB: PROFESSIONALE E FISCALE ═══ */}
                {nurseDetailTab === 'professionale' && (
                  <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5">
                    <h4 className="text-xs font-black text-indigo-700 uppercase tracking-wider mb-3 flex items-center gap-2">
                       Dati Fiscali e Assicurativi
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-4 text-sm">
                      {n.opi_provincia && (
                        <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 hover:shadow-sm transition-all">
                          <span className="text-[10px] text-indigo-400 block uppercase font-bold mb-0.5">Albo OPI</span>
                          <div className="font-medium text-slate-700">{n.opi_provincia} — <span className="text-indigo-600 font-bold">{n.opi_numero || 'N/D'}</span></div>
                          {(n.data_abilitazione || n.data_iscrizione_albo) && (
                            <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-1"><Calendar className="w-3 h-3"/> Iscr: {new Date(n.data_iscrizione_albo || n.data_abilitazione!).toLocaleDateString('it-IT')}</div>
                          )}
                        </div>
                      )}
                      {(n.assicurazione || n.polizza_massimale) && (
                        <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 hover:shadow-sm transition-all">
                          <span className="text-[10px] text-indigo-400 block uppercase font-bold mb-0.5">Assicurazione RCT</span>
                          <div className="font-medium text-slate-700">{n.assicurazione || 'Non specificata'}</div>
                          {(n.polizza_massimale || n.assicurazione_scadenza) && (
                            <div className="text-[10px] text-slate-500 mt-1 flex justify-between items-center">
                              {n.polizza_massimale && <span>Max: €{n.polizza_massimale}</span>}
                              {n.assicurazione_scadenza && <span className={`flex items-center gap-1 font-bold ${assurance.color === 'red' ? 'text-red-500' : assurance.color === 'amber' ? 'text-amber-500' : 'text-emerald-500'}`}><Calendar className="w-3 h-3"/> Scad: {new Date(n.assicurazione_scadenza).toLocaleDateString('it-IT')}</span>}
                            </div>
                          )}
                        </div>
                      )}
                      {n.iban && (
                        <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 hover:shadow-sm transition-all">
                          <span className="text-[10px] text-indigo-400 block uppercase font-bold mb-0.5">IBAN per Rimborsi</span>
                          <div className="font-mono text-xs font-bold text-slate-700 break-all">{n.iban}</div>
                        </div>
                      )}
                      {n.residenza_contratto && (
                        <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 hover:shadow-sm transition-all">
                          <span className="text-[10px] text-indigo-400 block uppercase font-bold mb-0.5">Residenza Fiscale (Per CP)</span>
                          <div className="font-medium text-slate-700 text-xs leading-tight">{n.residenza_contratto}</div>
                        </div>
                      )}
                    </div>
                    {isStaffPlus && (
                      <div className="mt-6 pt-5 border-t border-slate-100 flex justify-end">
                        <button
                          onClick={() => handleAiDraftContract(n)}
                          disabled={draftingAIContract}
                          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-700 hover:to-orange-600 text-white rounded-xl text-sm font-bold transition-all shadow-lg hover:shadow-xl shadow-red-500/30 disabled:opacity-50"
                          title="Scarica il modulo .docx ufficiale QuiCare compilato con i dati del database"
                        >
                          {draftingAIContract ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                          Scarica Contratto Ufficiale
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* ═══ TAB: DIARIO INFERMIERE ═══ */}
                {nurseDetailTab === 'diario' && (
                  <div className="space-y-4">
                    {/* Form aggiunta/modifica nota */}
                    {isStaffPlus && (
                      <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
                        <div className="text-xs font-bold text-slate-400 uppercase">{editingNurseDiaryId ? '✏️ Modifica Nota' : 'Nuova Nota'}</div>
                        <div className="grid grid-cols-3 gap-3">
                          <input type="text" value={nurseDiaryForm.titolo} onChange={e => setNurseDiaryForm({ ...nurseDiaryForm, titolo: e.target.value })}
                            placeholder="Titolo nota..." className="col-span-2 px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                          <div className="flex gap-2">
                            <select value={nurseDiaryForm.tipo} onChange={e => setNurseDiaryForm({ ...nurseDiaryForm, tipo: e.target.value })}
                              className="flex-1 px-2 py-2 border border-slate-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500">
                              <option value="appunto">📌 Appunto</option>
                              <option value="documentazione">📄 Documentazione</option>
                              <option value="feedback">🤝 Feedback</option>
                              <option value="richiamo">⚠️ Richiamo</option>
                              <option value="altro">Altro</option>
                            </select>
                            <input type="date" value={nurseDiaryForm.data} onChange={e => setNurseDiaryForm({ ...nurseDiaryForm, data: e.target.value })}
                              className="px-2 py-2 border border-slate-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                          </div>
                        </div>
                        <textarea value={nurseDiaryForm.contenuto} onChange={e => setNurseDiaryForm({ ...nurseDiaryForm, contenuto: e.target.value })}
                          rows={3} placeholder="Dettagli..."
                          className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none" />
                        <div className="flex items-center gap-2">
                          <button onClick={() => handleAddNurseDiaryEntry(n.id)}
                            className="flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-white bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl shadow-md hover:shadow-lg transition-all">
                            {editingNurseDiaryId ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {editingNurseDiaryId ? 'Salva Modifiche' : 'Aggiungi Nota'}
                          </button>
                          {editingNurseDiaryId && (
                            <button onClick={() => { setEditingNurseDiaryId(null); setNurseDiaryForm({ titolo: '', contenuto: '', tipo: 'appunto', data: new Date().toISOString().slice(0,10) }) }} className="px-4 py-2 text-sm font-bold text-slate-500 bg-slate-100 hover:bg-slate-200 rounded-xl transition-all">
                              Annulla
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {nurseDiary.length === 0 ? (
                      <div className="text-center py-12 text-slate-300">
                        <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p className="text-sm font-bold">Nessuna nota presente</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {nurseDiary.map(entry => {
                          const tipoColors: Record<string, string> = {
                            appunto: 'bg-blue-50 border-blue-200 text-blue-700',
                            documentazione: 'bg-indigo-50 border-indigo-200 text-indigo-700',
                            feedback: 'bg-emerald-50 border-emerald-200 text-emerald-700',
                            richiamo: 'bg-red-50 border-red-200 text-red-700',
                            altro: 'bg-slate-50 border-slate-200 text-slate-700',
                          }
                          const timeStr = entry.created_at ? new Date(entry.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : '';
                          
                          return (
                            <div key={entry.id} className={`bg-white rounded-xl border p-4 transition-all ${editingNurseDiaryId === entry.id ? 'border-indigo-500 shadow-md ring-1 ring-indigo-500' : 'border-slate-200 hover:border-indigo-300'}`}>
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className={`text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded border ${tipoColors[entry.tipo] || tipoColors.altro}`}>
                                      {entry.tipo}
                                    </span>
                                    <span className="text-xs font-bold text-slate-500">{new Date(entry.data).toLocaleDateString('it-IT')}</span>
                                    {timeStr && <span className="text-xs text-slate-400">🕒 {timeStr}</span>}
                                    {entry.created_by && <span className="text-xs text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100 flex items-center gap-1"><User className="w-3 h-3" /> {entry.created_by}</span>}
                                  </div>
                                  <div className="text-sm font-bold text-slate-800 mt-1">{entry.titolo}</div>
                                  {entry.contenuto && <div className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{entry.contenuto}</div>}
                                </div>
                                <div className="flex items-center gap-1">
                                  {isAdmin && (
                                    <button onClick={() => { setEditingNurseDiaryId(entry.id); setNurseDiaryForm({ titolo: entry.titolo, contenuto: entry.contenuto || '', tipo: entry.tipo || 'appunto', data: entry.data }); window.scrollTo({ top: 0, behavior: 'smooth' }) }}
                                      className="p-1.5 text-slate-300 hover:bg-indigo-50 hover:text-indigo-600 rounded-lg transition-all" title="Modifica"><Edit3 className="w-4 h-4" /></button>
                                  )}
                                  {isSuperAdmin && (
                                    <button onClick={() => handleDeleteNurseDiaryEntry(entry.id, n.id)}
                                      className="p-1.5 text-slate-300 hover:bg-red-50 hover:text-red-600 rounded-lg transition-all" title="Elimina"><Trash2 className="w-4 h-4" /></button>
                                  )}
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* ═══ TAB: DOCUMENTI ═══ */}
                {nurseDetailTab === 'documenti' && (
                  <div className="bg-white rounded-2xl p-6 border border-slate-200">
                    <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-3 flex items-center gap-2">📁 Archivio Documenti <button onClick={() => fetchNurseDocuments(n.id)} className="ml-auto text-indigo-500 text-xs hover:underline font-normal normal-case">{loadingDocs ? '⏳...' : '🔄 Aggiorna'}</button></h4>
                    {isStaffPlus && (
                      <div className="flex items-center gap-2 mb-4">
                        <button 
                          onClick={() => handleAiDraftEmail(n, nurseDocuments)}
                          disabled={draftingAIEmail}
                          className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-violet-500 to-indigo-600 hover:from-violet-600 hover:to-indigo-700 text-white rounded-xl text-xs font-bold transition-all shadow-md disabled:opacity-50"
                          title="L'A.I. legge cosa c'è in archivio e prepara la mail da inviare per le integrazioni necessarie. Si apre direttamente l'app di Posta predefinita"
                        >
                          {draftingAIEmail ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                          A.I. Manda Sollecito
                        </button>
                        <div className="w-px h-6 bg-slate-300 mx-1"></div>
                        <select value={docCategory} onChange={e => setDocCategory(e.target.value)} className="px-2 py-2 border border-slate-200 rounded-lg text-xs bg-slate-50">
                          {DOC_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.icon} {c.label}</option>)}
                        </select>
                        <label className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed rounded-xl text-xs font-bold cursor-pointer transition-all ${uploadingDoc ? 'border-slate-200 text-slate-400' : 'border-indigo-200 text-indigo-600 hover:bg-indigo-50'}`}>
                          {uploadingDoc ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Caricamento...</> : <><Plus className="w-3.5 h-3.5" /> Carica Nuovo File</>}
                          <input type="file" className="hidden" disabled={uploadingDoc} onChange={e => { const f = e.target.files?.[0]; if (f) handleUploadDocument(n.id, f); e.target.value = '' }} />
                        </label>
                      </div>
                    )}
                    {nurseDocuments.length > 0 ? (
                      <div className="space-y-2">{nurseDocuments.map((doc: any) => { 
                        const cat = DOC_CATEGORIES.find(c => c.key === doc.categoria); 
                        const isOfficeDoc = doc.nome?.toLowerCase().match(/\.(doc|docx|xls|xlsx|ppt|pptx)$/);
                        const viewUrl = isOfficeDoc && doc.url ? `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(doc.url)}` : doc.url;
                        return (
                        <div key={doc.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl group hover:shadow-sm transition-all border border-slate-100">
                          <span className="text-lg">{cat?.icon || '📁'}</span>
                          <div className="flex-1 min-w-0"><div className="text-sm font-medium text-slate-700 truncate">{doc.nome}</div><div className="text-[10px] text-slate-400">{cat?.label} • {new Date(doc.created_at).toLocaleDateString('it-IT')}{doc.file_size && <> • {(doc.file_size / 1024).toFixed(0)} KB</>}</div></div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {doc.url && <a href={viewUrl} target="_blank" rel="noreferrer" className="p-2 hover:bg-indigo-100 rounded-lg text-indigo-600 transition-colors" title="Visualizza documento"><ExternalLink className="w-4 h-4" /></a>}
                            {isStaffPlus && <button onClick={() => handleDeleteDocument(doc.id, n.id)} className="p-2 hover:bg-red-100 rounded-lg text-red-500 transition-colors" title="Elimina"><Trash2 className="w-4 h-4" /></button>}
                          </div>
                        </div>
                      ) })}</div>
                    ) : (
                      <div className="text-center py-8 text-sm text-slate-400 bg-slate-50 rounded-xl border border-dashed border-slate-200">{loadingDocs ? '⏳ Caricamento...' : '📭 Nessun documento.'}</div>
                    )}
                  </div>
                )}

                {/* ═══ TAB: PAZIENTI E PERFORMANCE ═══ */}
                {nurseDetailTab === 'pazienti' && (
                  <div className="space-y-4">
                    <div className="bg-white rounded-2xl p-6 border border-slate-200">
                      <h4 className="text-sm font-black text-rose-700 uppercase tracking-wider mb-4 flex items-center gap-2">
                        Pazienti in Carico ({nursePatients.length})
                      </h4>
                      {nursePatients.length === 0 ? (
                        <p className="text-sm text-slate-400 italic">Nessun paziente assegnato al momento</p>
                      ) : (
                        <div className="space-y-3">
                          {nursePatients.map(({ patient: p, appointments: appts }) => {
                            const total = appts.length
                            const done = appts.filter(a => a.completato).length
                            const progress = total > 0 ? Math.round((done / total) * 100) : 0
                            const nextAppt = appts.filter(a => !a.completato && a.data_prevista).sort((a, b) => new Date(a.data_prevista!).getTime() - new Date(b.data_prevista!).getTime())[0]
                            const now = new Date(); now.setHours(0,0,0,0)
                            const daysNext = nextAppt?.data_prevista ? Math.round((new Date(nextAppt.data_prevista).getTime() - now.getTime()) / 86400000) : null
                            return (
                              <div key={p.id} className="bg-slate-50 rounded-xl p-4 border border-slate-100 hover:shadow-sm transition-all">
                                <div className="flex items-center gap-3">
                                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white font-black text-sm shadow-sm ${p.percorso_tipo === 'naive' ? 'bg-gradient-to-br from-blue-500 to-indigo-600' : 'bg-gradient-to-br from-purple-500 to-pink-600'}`}>
                                    {(p.cognome||'?')[0]}{(p.nome||'?')[0]}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-bold text-slate-800">{p.cognome} {p.nome}</div>
                                    <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                                      <span className={`font-bold px-1.5 py-0.5 rounded text-[10px] ${p.percorso_tipo === 'naive' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'}`}>
                                        {p.percorso_tipo === 'naive' ? 'NAÏVE' : 'SWITCH'}
                                      </span>
                                      {p.telefono && <span>📞 {p.telefono}</span>}
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <div className="text-sm font-black text-slate-600">{progress}%</div>
                                    <div className="w-20 h-2 bg-slate-200 rounded-full overflow-hidden mt-1">
                                      <div className={`h-full rounded-full transition-all ${progress === 100 ? 'bg-green-500' : 'bg-indigo-500'}`} style={{ width: `${progress}%` }} />
                                    </div>
                                    <div className="text-xs text-slate-400 mt-1">{done}/{total} appt</div>
                                  </div>
                                </div>
                                {(p.caregiver_nome || p.indirizzo || nextAppt) && (
                                  <div className="mt-3 pt-3 border-t border-slate-200 flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                                    {p.indirizzo && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-slate-400"/> {p.indirizzo}</span>}
                                    {p.caregiver_nome && <span className="flex items-center gap-1"><Heart className="w-3.5 h-3.5 text-rose-400"/> {p.caregiver_nome} {p.caregiver_cognome || ''} {p.caregiver_telefono ? `(${p.caregiver_telefono})` : ''}</span>}
                                    {nextAppt && (
                                      <span className={`ml-auto font-bold px-2 py-1 rounded text-[10px] ${
                                        daysNext !== null && daysNext < 0 ? 'bg-red-100 text-red-700 border border-red-200' :
                                        daysNext !== null && daysNext <= 7 ? 'bg-amber-100 text-amber-700 border border-amber-200' :
                                        'bg-slate-100 text-slate-600 border border-slate-200'
                                      }`}>
                                        🗓️ Prossimo: {nextAppt.descrizione?.slice(0, 30)}
                                        {daysNext !== null && (daysNext < 0 ? ` (${Math.abs(daysNext)}g fa!)` : daysNext === 0 ? ' (OGGI!)' : ` (${daysNext}g)`)}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {n.created_at && <div className="text-[10px] text-slate-300 text-center uppercase tracking-wider mt-6">Profilo creato il {new Date(n.created_at).toLocaleDateString('it-IT')} • ID: {n.id.substring(0, 8)}</div>}
              </div>
            </div>
          </div>
        )
      })()}
    </div>
  )
}

// Premium form components
function FormSection({ title, icon, color = 'slate', children }: { title: string; icon: React.ReactNode; color?: 'indigo' | 'emerald' | 'amber' | 'slate' | 'violet' | 'cyan', children: React.ReactNode }) {
  const colorMap: Record<string, string> = {
    indigo: 'bg-indigo-50/50 text-indigo-700 border-indigo-100',
    emerald: 'bg-emerald-50/50 text-emerald-700 border-emerald-100',
    amber: 'bg-amber-50/50 text-amber-700 border-amber-100',
    violet: 'bg-violet-50/50 text-violet-700 border-violet-100',
    cyan: 'bg-cyan-50/50 text-cyan-700 border-cyan-100',
    slate: 'bg-slate-50 text-slate-700 border-slate-100'
  }
  
  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-slate-200/60 overflow-hidden shadow-sm">
      <div className={`px-4 py-3 flex items-center gap-2 border-b ${colorMap[color] || colorMap.slate}`}>
        {icon}
        <h4 className="text-xs font-black uppercase tracking-wider">{title}</h4>
      </div>
      <div className="p-5">
        {children}
      </div>
    </div>
  )
}

function FormField({ label, value, onChange, type = 'text', required = false }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; required?: boolean
}) {
  return (
    <div>
      <label className="block text-[10px] font-black text-slate-500 mb-1.5 uppercase tracking-wider">{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full px-3.5 py-3 border border-slate-200/80 rounded-xl text-sm bg-white shadow-sm focus:border-indigo-400 focus:ring-4 focus:ring-indigo-400/20 outline-none transition-all placeholder-slate-300"
        placeholder={`Inserisci ${label.toLowerCase().replace('*', '').trim()}`}
      />
    </div>
  )
}


